import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import RefreshIcon from "@mui/icons-material/Refresh";
import RuleIcon from "@mui/icons-material/Rule";
import SecurityIcon from "@mui/icons-material/Security";
import TuneIcon from "@mui/icons-material/Tune";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { toast } from "react-toastify";
import { axiosInstance } from "../../../../../api/axios";
import { AutoPilotGuide } from "../../../components/common/AutoPilotGuide";
import { EmptyState } from "../../../components/common/EmptyState";
import { ImageUploadBox } from "../../../components/common/ImageUploadBox";

const STEPS = [
  {
    label: "대상 선택",
    icon: <PrecisionManufacturingIcon />,
  },
  {
    label: "위험성평가",
    icon: <TuneIcon />,
  },
  {
    label: "감소대책",
    icon: <WarningAmberIcon />,
  },
  {
    label: "확인/승인",
    icon: <AssignmentTurnedInIcon />,
  },
];

const normalizeWizardStepIndex = (value) => {
  const stepIndex = Number(value);
  if (!Number.isInteger(stepIndex)) return 0;
  return Math.min(Math.max(stepIndex, 0), STEPS.length - 1);
};

// eslint-disable-next-line no-unused-vars
const INITIAL_GROUPS = [
  {
    id: "cnc-assembly",
    equipment: "CNC 3호기",
    process: "조립 공정",
    line: "A공장 가공팀",
    hazards: [
      {
        id: "hz-cnc-rotating",
        title: "회전체(모터)에 손이 끼일 위험",
        source: "기계적 위험",
      },
      {
        id: "hz-cnc-coolant",
        title: "절삭유 비산에 의한 눈 상해 위험",
        source: "화학/비산 위험",
      },
    ],
  },
  {
    id: "press-cutting",
    equipment: "프레스 2호기",
    process: "절단 공정",
    line: "A공장 절단팀",
    hazards: [
      {
        id: "hz-press-crush",
        title: "프레스 작동 시 협착 위험",
        source: "협착 위험",
      },
    ],
  },
  {
    id: "conveyor-assembly",
    equipment: "조립 A라인 컨베이어",
    process: "조립 공정",
    line: "A공장 조립팀",
    hazards: [
      {
        id: "hz-conveyor-fall",
        title: "이동 중 부품 낙하로 인한 발등 상해 위험",
        source: "낙하 위험",
      },
    ],
  },
];

// eslint-disable-next-line no-unused-vars
const ASSIGNEES = [
  "홍길동 (설비팀)",
  "김안전 (안전관리팀)",
  "이영수 (생산기술팀)",
  "박관리 (공무팀)",
];

const WIZARD_DRAFT_STORAGE_PREFIX = "risk-assessment-wizard-drafts";
const WIZARD_DRAFT_SCHEMA_VERSION = 3;
// eslint-disable-next-line no-unused-vars
const INITIAL_SELECTED_HAZARD_IDS = [];

const DEFAULT_RISK_CRITERIA = {
  frequencies: [1, 2, 3, 4, 5].map((level) => ({
    frequency_id: level,
    frequency_level: level,
    frequency_description: String(level),
  })),
  importancies: [1, 2, 3, 4].map((level) => ({
    importancy_id: level,
    importancy_level: level,
    importancy_description: String(level),
  })),
  standards: [
    {
      standard_id: 1,
      risk_level_min: 1,
      risk_level_max: 3,
      manage_criteria: "매우낮음",
      requires_improve: 0,
    },
    {
      standard_id: 2,
      risk_level_min: 4,
      risk_level_max: 6,
      manage_criteria: "낮음",
      requires_improve: 0,
    },
    {
      standard_id: 3,
      risk_level_min: 7,
      risk_level_max: 8,
      manage_criteria: "보통",
      requires_improve: 0,
    },
    {
      standard_id: 4,
      risk_level_min: 9,
      risk_level_max: 12,
      manage_criteria: "약간높음",
      requires_improve: 1,
    },
    {
      standard_id: 5,
      risk_level_min: 13,
      risk_level_max: 15,
      manage_criteria: "높음",
      requires_improve: 1,
    },
    {
      standard_id: 6,
      risk_level_min: 16,
      risk_level_max: 20,
      manage_criteria: "매우높음",
      requires_improve: 1,
    },
  ],
};

const EMPTY_RISK_CRITERIA = {
  frequencies: [],
  importancies: [],
  standards: [],
};

const shellSx = {
  border: "1px solid",
  borderColor: "rgba(148, 163, 184, 0.32)",
  borderRadius: "10px",
  bgcolor: "rgba(255, 255, 255, 0.96)",
  boxShadow: "0 18px 46px rgba(15, 23, 42, 0.06)",
};

const panelSx = {
  border: "1px solid",
  borderColor: "rgba(148, 163, 184, 0.28)",
  borderRadius: "10px",
  bgcolor: "#fff",
};

const getToday = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const getDefaultDueDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

// eslint-disable-next-line no-unused-vars
const cloneJson = (value) => JSON.parse(JSON.stringify(value));

const createDraftId = () =>
  `wizard-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeWizardGroupKeyPart = (value, fallback = "0") => {
  const key = String(value || "")
    .replace(/^wizard-/, "")
    .replace(/[^A-Za-z0-9_-]/g, "")
    .slice(0, 32);
  return key || fallback;
};

const createWizardGroupKey = ({
  scope = "asset",
  sourceLocationId,
  registerAssetId,
  registerCauseId,
  draftId,
} = {}) => {
  const prefix =
    scope === "line"
      ? `L${normalizeWizardGroupKeyPart(sourceLocationId, "0").slice(0, 12)}`
      : `A${normalizeWizardGroupKeyPart(
          registerAssetId || registerCauseId,
          "0"
        ).slice(0, 12)}`;
  const suffix = normalizeWizardGroupKeyPart(draftId, "new");
  return `WZ-${prefix}-${suffix}`.slice(0, 64);
};

const getDraftStorageKey = (groupCode, projectCode) =>
  `${WIZARD_DRAFT_STORAGE_PREFIX}:${groupCode || "unknown"}:${
    projectCode || "unknown"
  }`;

const getStorage = () => {
  if (typeof window === "undefined" || !window.localStorage) return null;
  return window.localStorage;
};

const readWizardDrafts = (storageKey) => {
  try {
    const storage = getStorage();
    if (!storage) return [];
    const parsed = JSON.parse(storage.getItem(storageKey) || "[]");
    return Array.isArray(parsed)
      ? parsed.sort((a, b) =>
          String(b.updatedAt || "").localeCompare(String(a.updatedAt || ""))
        )
      : [];
  } catch {
    return [];
  }
};

const writeWizardDrafts = (storageKey, drafts) => {
  try {
    const storage = getStorage();
    if (!storage) return;
    storage.setItem(storageKey, JSON.stringify(drafts));
  } catch {
    // localStorage may be unavailable or full.
  }
};

const isExistingWorkItemDraft = (draft = {}) => {
  const groups = Array.isArray(draft.groups) ? draft.groups : [];
  const improvementGroups = Array.isArray(draft.improvementGroups)
    ? draft.improvementGroups
    : [];

  return (
    groups.length === 0 &&
    improvementGroups.length === 1 &&
    improvementGroups.some((group) =>
      ["approval", "improvement"].includes(group?.workflowType)
    )
  );
};

const normalizeDraftActiveStep = (draft = {}) => {
  const stepIndex = Number(draft.activeStep || 0);
  const schemaVersion = Number(draft.schemaVersion || 0);
  if (schemaVersion >= WIZARD_DRAFT_SCHEMA_VERSION) {
    return normalizeWizardStepIndex(stepIndex);
  }

  // v2 drafts used 3 screens: target/risk -> measure -> approve.
  if (schemaVersion === 2) {
    if (stepIndex <= 0) return 0;
    if (stepIndex === 1) return 2;
    return 3;
  }

  // Older drafts already used 4 screens: target -> risk -> measure -> approve.
  return normalizeWizardStepIndex(stepIndex);
};

const normalizeDraftFile = (file) => {
  if (isUploadableFile(file)) return file;
  return null;
};

const normalizeDraftMeasures = (measures = {}) =>
  Object.entries(measures).reduce((acc, [hazardId, measure]) => {
    acc[hazardId] = {
      ...measure,
      beforeFile: normalizeDraftFile(measure?.beforeFile),
      afterFile: normalizeDraftFile(measure?.afterFile),
    };
    return acc;
  }, {});

const formatDraftTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const flattenHazards = (groups) =>
  groups.flatMap((group) =>
    group.hazards.map((hazard) => ({
      ...hazard,
      groupId: group.id,
      equipment: group.equipment,
      process: group.process,
      line: group.line,
    }))
  );

const countHazardsInGroups = (groups = []) =>
  groups.reduce(
    (total, group) =>
      total + (Array.isArray(group.hazards) ? group.hazards.length : 0),
    0
  );

const isLineEvaluationGroup = (group = {}) => {
  if (Array.isArray(group.location_ids) && group.location_ids.length) return true;
  if (Array.isArray(group.location_names) && group.location_names.length) return true;
  if (group.location_summary) return true;
  return (group.hazards || []).some(
    (hazard) => hazard.eval_scope === "line" || hazard.source_location_id
  );
};

const getHazardLineEntries = (hazard = {}, group = {}) => {
  const entries = [];
  const pushEntry = (id, name) => {
    const numericId = Number(id);
    const entryId = Number.isInteger(numericId) && numericId > 0 ? numericId : null;
    const entryName = String(name || "").trim();
    if (!entryId && !entryName) return;
    const key = entryId ? `id-${entryId}` : `name-${entryName}`;
    if (entries.some((entry) => entry.key === key)) return;
    entries.push({
      key,
      id: entryId,
      name: entryName || `라인 ${entryId}`,
    });
  };

  if (hazard.source_location_id || hazard.source_location_name) {
    pushEntry(hazard.source_location_id, hazard.source_location_name);
  }

  const hazardIds = Array.isArray(hazard.location_ids) ? hazard.location_ids : [];
  const hazardNames = Array.isArray(hazard.location_names)
    ? hazard.location_names
    : hazard.location_summary
    ? [hazard.location_summary]
    : [];
  const groupIds = Array.isArray(group.location_ids) ? group.location_ids : [];
  const groupNames = Array.isArray(group.location_names)
    ? group.location_names
    : group.location_summary
    ? [group.location_summary]
    : [];
  const ids = hazardIds.length ? hazardIds : groupIds;
  const names = hazardNames.length ? hazardNames : groupNames;
  const maxLength = Math.max(ids.length, names.length);

  for (let index = 0; index < maxLength; index += 1) {
    pushEntry(ids[index], names[index]);
  }

  return entries;
};

const createLineEvaluationGroups = (groups = []) => {
  const lineMap = new Map();

  groups.filter(isLineEvaluationGroup).forEach((group) => {
    (group.hazards || []).forEach((hazard) => {
      getHazardLineEntries(hazard, group).forEach((entry) => {
        const lineKey = entry.key;
        if (!lineMap.has(lineKey)) {
          lineMap.set(lineKey, {
            id: `line-${lineKey}`,
            scopeType: "line",
            workflowType: group.workflowType,
            equipment: entry.name,
            process: "라인 평가",
            line: "",
            processes: [],
            process_count: 0,
            location_ids: entry.id ? [entry.id] : [],
            location_names: entry.name ? [entry.name] : [],
            location_summary: entry.name,
            hazards: [],
            _hazardKeys: new Set(),
            _equipmentNames: new Set(),
            _processNames: new Set(),
          });
        }

        const lineGroup = lineMap.get(lineKey);
        const hazardKey = hazard.eval_id
          ? `eval-${hazard.eval_id}`
          : hazard.register_cause_id
          ? `cause-${hazard.register_cause_id}`
          : hazard.id;
        const lineHazardId = `line-${lineKey}-${hazard.id}`;

        if (!lineGroup._hazardKeys.has(hazardKey)) {
          lineGroup._hazardKeys.add(hazardKey);
          lineGroup.hazards.push({
            ...hazard,
            id: lineHazardId,
            source_hazard_id: hazard.id,
            eval_id: hazard.eval_scope === "line" ? hazard.eval_id : undefined,
            eval_scope: "line",
            source_location_id: entry.id || hazard.source_location_id || null,
            source_location_name:
              entry.name || hazard.source_location_name || "",
            location_ids: entry.id ? [entry.id] : hazard.location_ids || [],
            location_names: entry.name
              ? [entry.name]
              : hazard.location_names || [],
            location_summary: entry.name || hazard.location_summary || "",
            wizard_step: hazard.wizard_step || 0,
            wizard_group_key: hazard.wizard_group_key || "",
            line_group_name: entry.name,
          });
        }

        const equipmentName = hazard.equipment || group.equipment;
        const processName = hazard.process || group.process;
        if (equipmentName) lineGroup._equipmentNames.add(equipmentName);
        if (processName) lineGroup._processNames.add(processName);
      });
    });
  });

  return Array.from(lineMap.values()).map(
    ({ _hazardKeys, _equipmentNames, _processNames, ...lineGroup }) => {
      const equipmentCount = _equipmentNames.size;
      const processCount = _processNames.size;
      return {
        ...lineGroup,
        process:
          processCount > 1
            ? `${processCount}개 공정`
            : Array.from(_processNames)[0] || "라인 평가",
        process_count: processCount,
        line: [
          equipmentCount ? `${equipmentCount}대 설비` : "",
          processCount ? `${processCount}개 공정` : "",
          `${lineGroup.hazards.length}개 위험요인`,
        ]
          .filter(Boolean)
          .join(" · "),
      };
    }
  );
};

const getEvaluationGroupsByScope = (groups = [], scope = "all") => {
  const lineGroups = createLineEvaluationGroups(groups);

  if (scope === "line") return lineGroups;
  if (scope === "asset") return groups;
  return [...groups, ...lineGroups];
};

const buildWorkerProposalSourceMap = (proposals = []) => {
  const map = new Map();
  (proposals || [])
    .filter(
      (proposal) =>
        proposal.status === "approved" && proposal.approved_danger_cause_id
    )
    .forEach((proposal) => {
      const dangerCauseId = Number(proposal.approved_danger_cause_id);
      const current = map.get(dangerCauseId);
      if (!current) {
        map.set(dangerCauseId, {
          count: 1,
          latest_worker_proposal_id: Number(proposal.proposal_id),
        });
        return;
      }
      current.count += 1;
      current.latest_worker_proposal_id = Math.max(
        Number(current.latest_worker_proposal_id || 0),
        Number(proposal.proposal_id || 0)
      );
    });
  return map;
};

const getWizardMachineGroupId = (row, workflowType = "pending") => {
  const machineId = row.machine_id ? Number(row.machine_id) : null;
  if (machineId) return `${workflowType}-machine-${machineId}`;

  const registerAssetId = row.register_asset_id
    ? Number(row.register_asset_id)
    : null;
  if (registerAssetId) return `${workflowType}-asset-${registerAssetId}`;

  return `${workflowType}-asset-${row.machine_id || "none"}-${
    row.process_id || "none"
  }`;
};

const addOrMergeWizardHazard = (group, hazard, matchKey) => {
  const existing = group.hazards.find(
    (item) => item[matchKey] && String(item[matchKey]) === String(hazard[matchKey])
  );

  if (!existing) {
    group.hazards.push({ ...hazard, duplicate_count: 1 });
    return;
  }

  existing.duplicate_count = Number(existing.duplicate_count || 1) + 1;
  existing.worker_proposal_count = Math.max(
    Number(existing.worker_proposal_count || 0),
    Number(hazard.worker_proposal_count || 0)
  );
  existing.latest_worker_proposal_id =
    Math.max(
      Number(existing.latest_worker_proposal_id || 0),
      Number(hazard.latest_worker_proposal_id || 0)
    ) || existing.latest_worker_proposal_id;
  existing.location_ids = Array.from(
    new Set([...(existing.location_ids || []), ...(hazard.location_ids || [])])
  );
  existing.location_names = Array.from(
    new Set([...(existing.location_names || []), ...(hazard.location_names || [])])
  );
  existing.location_summary = existing.location_names.length
    ? existing.location_names.join(", ")
    : existing.location_summary || hazard.location_summary || "";
};

const updateWizardGroupProcessSummary = (group, row) => {
  const processName = row.process_name || "-";
  const processKey = String(row.process_id || processName);
  if (!group._processKeys.includes(processKey)) {
    group._processKeys.push(processKey);
    group.processes.push({
      process_id: row.process_id ? Number(row.process_id) : null,
      process_name: processName,
      process_description: row.process_description || "",
      process_index: row.process_index,
    });
  }

  const processNames = group.processes.map((item) => item.process_name);
  group.process_count = processNames.length;
  group.process =
    processNames.length > 1 ? `${processNames.length}개 공정` : processNames[0];

  const manageCode = row.machine_manage_code
    ? `관리번호 ${row.machine_manage_code}`
    : "";
  const processDetail =
    processNames.length > 1
      ? processNames.join(", ")
      : row.process_description || "";
  group.line = [manageCode, processDetail].filter(Boolean).join(" · ");
};

const parseWizardLocationIds = (value) =>
  String(value || "")
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isInteger(item) && item > 0);

const parseWizardLocationNames = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const updateWizardGroupLocationSummary = (group, row) => {
  parseWizardLocationIds(row.location_ids).forEach((locationId) => {
    if (!group._locationKeys.includes(locationId)) {
      group._locationKeys.push(locationId);
      group.location_ids.push(locationId);
    }
  });

  parseWizardLocationNames(row.location_names).forEach((locationName) => {
    if (!group.location_names.includes(locationName)) {
      group.location_names.push(locationName);
    }
  });

  group.location_summary = group.location_names.length
    ? group.location_names.join(", ")
    : "";
};

const normalizePendingHazardGroups = (
  rows = [],
  workerProposalSourceMap = new Map()
) => {
  const groupMap = new Map();

  rows.forEach((row) => {
    const registerAssetId = Number(row.register_asset_id);
    const machineName = row.safe_machine_name || row.machine_name || "-";
    const processName = row.process_name || "-";
    const groupId = getWizardMachineGroupId(row, "pending");
    const workerProposalSource = workerProposalSourceMap.get(
      Number(row.danger_cause_id)
    );
    const isWorkerProposal =
      Number(row.worker_proposal_source || 0) === 1 ||
      Boolean(workerProposalSource);

    if (!groupMap.has(groupId)) {
      groupMap.set(groupId, {
        id: groupId,
        register_asset_id: registerAssetId || null,
        machine_id: row.machine_id ? Number(row.machine_id) : null,
        process_id: row.process_id ? Number(row.process_id) : null,
        equipment: machineName,
        process: processName,
        line: row.machine_manage_code
          ? `관리번호 ${row.machine_manage_code}`
          : row.process_description || "",
        processes: [],
        _processKeys: [],
        location_ids: [],
        location_names: [],
        _locationKeys: [],
        location_summary: "",
        process_count: 1,
        hazards: [],
      });
    }

    updateWizardGroupProcessSummary(groupMap.get(groupId), row);
    updateWizardGroupLocationSummary(groupMap.get(groupId), row);

    addOrMergeWizardHazard(groupMap.get(groupId), {
      id: String(row.register_cause_id),
      register_cause_id: Number(row.register_cause_id),
      register_asset_id: registerAssetId || null,
      danger_cause_id: row.danger_cause_id ? Number(row.danger_cause_id) : null,
      title: row.danger_description || "-",
      source:
        [row.danger_category, row.danger_cause].filter(Boolean).join(" / ") ||
        "유해·위험요인",
      worker_proposal_source: isWorkerProposal ? 1 : 0,
      worker_proposal_count:
        Number(row.worker_proposal_count || 0) ||
        workerProposalSource?.count ||
        0,
      latest_worker_proposal_id: row.latest_worker_proposal_id
        ? Number(row.latest_worker_proposal_id)
        : workerProposalSource?.latest_worker_proposal_id || null,
      equipment: machineName,
      process: processName,
      process_id: row.process_id ? Number(row.process_id) : null,
      location_ids: parseWizardLocationIds(row.location_ids),
      location_names: parseWizardLocationNames(row.location_names),
      location_summary: row.location_names || "",
      eval_scope: row.eval_scope || "asset",
      source_location_id: row.source_location_id
        ? Number(row.source_location_id)
        : null,
      source_location_name: row.source_location_name || "",
      wizard_step: row.wizard_step ? Number(row.wizard_step) : 0,
      wizard_group_key: row.wizard_group_key || "",
      line: row.machine_manage_code
        ? `관리번호 ${row.machine_manage_code}`
        : row.process_description || "",
    }, "register_cause_id");
  });

  return Array.from(groupMap.values()).map(
    ({ _processKeys, _locationKeys, ...group }) => group
  );
};

const normalizeImprovementHazardGroups = (
  rows = [],
  workflowType = "improvement"
) => {
  const groupMap = new Map();

  rows.forEach((row) => {
    const registerAssetId = Number(row.register_asset_id);
    const machineName = row.safe_machine_name || row.machine_name || "-";
    const processName = row.process_name || "-";
    const groupId = getWizardMachineGroupId(row, workflowType);

    if (!groupMap.has(groupId)) {
      groupMap.set(groupId, {
        id: groupId,
        workflowType,
        register_asset_id: registerAssetId || null,
        machine_id: row.machine_id ? Number(row.machine_id) : null,
        process_id: row.process_id ? Number(row.process_id) : null,
        equipment: machineName,
        process: processName,
        line: row.machine_manage_code
          ? `관리번호 ${row.machine_manage_code}`
          : row.process_description || "",
        processes: [],
        _processKeys: [],
        location_ids: [],
        location_names: [],
        _locationKeys: [],
        location_summary: "",
        process_count: 1,
        hazards: [],
      });
    }

    updateWizardGroupProcessSummary(groupMap.get(groupId), row);
    updateWizardGroupLocationSummary(groupMap.get(groupId), row);

    addOrMergeWizardHazard(groupMap.get(groupId), {
      id: `${workflowType}-${row.eval_id}`,
      workflowType,
      eval_id: Number(row.eval_id),
      eval_status: Number(row.eval_status || 0),
      register_cause_id: Number(row.register_cause_id),
      register_asset_id: registerAssetId || null,
      danger_cause_id: row.danger_cause_id ? Number(row.danger_cause_id) : null,
      title: row.danger_description || "-",
      source:
        [row.danger_category, row.danger_cause].filter(Boolean).join(" / ") ||
        "유해·위험요인",
      equipment: machineName,
      process: processName,
      process_id: row.process_id ? Number(row.process_id) : null,
      location_ids: parseWizardLocationIds(row.location_ids),
      location_names: parseWizardLocationNames(row.location_names),
      location_summary: row.location_names || "",
      eval_scope: row.eval_scope || "asset",
      source_location_id: row.source_location_id
        ? Number(row.source_location_id)
        : null,
      source_location_name: row.source_location_name || "",
      wizard_step: row.wizard_step ? Number(row.wizard_step) : 0,
      wizard_group_key: row.wizard_group_key || "",
      line: row.machine_manage_code
        ? `관리번호 ${row.machine_manage_code}`
        : row.process_description || "",
      frequency_id: row.frequency_id ? Number(row.frequency_id) : null,
      importancy_id: row.importancy_id ? Number(row.importancy_id) : null,
      standard_id: row.standard_id ? Number(row.standard_id) : null,
      manage_criteria: row.manage_criteria || "",
      requires_improve: Number(row.requires_improve || 0),
      improve_id: row.improve_id ? Number(row.improve_id) : null,
      improve_status: Number(row.improve_status || 0),
      improve_plan: row.improve_plan || "",
      improve_before: row.improve_before || "",
      improve_after: row.improve_after || "",
      improve_after_risk: row.improve_after_risk
        ? Number(row.improve_after_risk)
        : "",
      expected_date: row.expected_date || "",
      comp_date: row.comp_date || "",
      emp_id: row.emp_id ? String(row.emp_id) : "",
      emp_name: row.emp_name || "",
      eval_date: row.eval_date || "",
      approve_date: row.approve_date || "",
      eval_updated_at: row.eval_updated_at || "",
      improve_updated_at: row.improve_updated_at || "",
    }, "eval_id");
  });

  return Array.from(groupMap.values()).map(
    ({ _processKeys, _locationKeys, ...group }) => group
  );
};

const buildStepItemPatchMap = (rows = []) =>
  rows.reduce((map, row) => {
    const registerCauseId = Number(row?.register_cause_id);
    if (!registerCauseId) return map;
    map.set(registerCauseId, {
      eval_id: row.eval_id ? Number(row.eval_id) : null,
      eval_status: Number(row.eval_status || 0),
      frequency_id: row.frequency_id ? Number(row.frequency_id) : null,
      importancy_id: row.importancy_id ? Number(row.importancy_id) : null,
      standard_id: row.standard_id ? Number(row.standard_id) : null,
      manage_criteria: row.manage_criteria || "",
      requires_improve: Number(row.requires_improve || 0),
      improve_id: row.improve_id ? Number(row.improve_id) : null,
      improve_status: Number(row.improve_status || 0),
      improve_plan: row.improve_plan || "",
      improve_before: row.improve_before || "",
      improve_after: row.improve_after || "",
      improve_after_risk: row.improve_after_risk
        ? Number(row.improve_after_risk)
        : "",
      expected_date: row.expected_date || "",
      comp_date: row.comp_date || "",
      emp_id: row.emp_id ? Number(row.emp_id) : "",
      eval_date: row.eval_date || "",
      approve_date: row.approve_date || "",
      wizard_step: row.wizard_step ? Number(row.wizard_step) : 0,
      wizard_group_key: row.wizard_group_key || "",
      eval_scope: row.eval_scope || "asset",
      source_location_id: row.source_location_id
        ? Number(row.source_location_id)
        : null,
      source_location_name: row.source_location_name || "",
    });
    return map;
  }, new Map());

const mergeStepItemPatchIntoHazard = (hazard, patchMap) => {
  const patch = patchMap.get(Number(hazard?.register_cause_id));
  if (!patch) return hazard;
  return {
    ...hazard,
    ...patch,
    eval_id: patch.eval_id || hazard.eval_id,
    eval_status: Math.max(Number(hazard.eval_status || 0), patch.eval_status),
    wizard_step: Math.max(Number(hazard.wizard_step || 0), patch.wizard_step),
    wizard_group_key: patch.wizard_group_key || hazard.wizard_group_key || "",
    eval_scope: patch.eval_scope || hazard.eval_scope || "asset",
    source_location_id:
      patch.source_location_id || hazard.source_location_id || null,
    source_location_name:
      patch.source_location_name || hazard.source_location_name || "",
  };
};

const mergeStepItemPatchesIntoGroups = (groups = [], patchMap) =>
  groups.map((group) => ({
    ...group,
    hazards: Array.isArray(group.hazards)
      ? group.hazards.map((hazard) =>
          mergeStepItemPatchIntoHazard(hazard, patchMap)
        )
      : [],
  }));

const isImprovementRequiredRow = (row) => {
  const evalStatus = Number(row?.eval_status || 0);
  const requiresImprove =
    Number(row?.requires_improve || 0) === 1 || Boolean(row?.improve_id);
  return evalStatus >= 20 && requiresImprove;
};

const isImprovementCompletedRow = (row = {}) =>
  Number(row?.improve_status || 0) >= 20 ||
  Boolean(row?.improve_after && row?.comp_date);

const isImprovementProgressRow = (row) =>
  isImprovementRequiredRow(row) && !isImprovementCompletedRow(row);

const getProgressEvaluationChipMeta = (hazard = {}, measure = {}) => {
  if (!isEvaluationLocked(hazard)) {
    return {
      label: "대상 등록",
      color: "info",
      variant: "outlined",
    };
  }

  const needsImprovement =
    Number(hazard.requires_improve || 0) === 1 || Boolean(hazard.improve_id);
  if (!needsImprovement) {
    return {
      label: "평가 완료",
      color: "success",
      variant: "filled",
    };
  }

  if (isMeasureLocked(hazard, measure)) {
    return {
      label: "개선 완료",
      color: "success",
      variant: "filled",
    };
  }

  if (hasSavedMeasurePlan(hazard, measure)) {
    const statusMeta = getMeasureStatusMeta(hazard, measure);
    return {
      label: statusMeta.label || "개선 중",
      color: statusMeta.color === "default" ? "info" : statusMeta.color,
      variant: "filled",
    };
  }

  return {
    label: "개선대책 필요",
    color: "warning",
    variant: "filled",
  };
};

const getLineWorkItemKey = (row = {}) => {
  if (row.eval_scope !== "line" && !row.source_location_id) return null;
  const wizardGroupKey = String(row.wizard_group_key || "").trim();
  if (wizardGroupKey) return `group-${wizardGroupKey}`;
  const locationId = row.source_location_id
    ? Number(row.source_location_id)
    : null;
  if (locationId) return `id-${locationId}`;
  const locationName = String(row.source_location_name || "").trim();
  return locationName ? `name-${locationName}` : null;
};

const getLineWorkItemName = (rows = [], fallback = "라인 평가") => {
  const sourceRow = rows.find((row) => row.source_location_name) || rows[0] || {};
  return sourceRow.source_location_name || fallback;
};

const groupImprovementWorkItems = (rows = []) => {
  const directItems = [];
  const lineGroups = new Map();

  rows.filter(isImprovementRequiredRow).forEach((row) => {
    const lineKey = getLineWorkItemKey(row);
    if (!lineKey) {
      if (isImprovementProgressRow(row)) directItems.push(row);
      return;
    }

    if (!lineGroups.has(lineKey)) {
      lineGroups.set(lineKey, {
        id: `line-improvement-${lineKey}`,
        isLineWorkItemGroup: true,
        eval_scope: "line",
        source_location_id: row.source_location_id
          ? Number(row.source_location_id)
          : null,
        source_location_name: row.source_location_name || "",
        wizard_step: row.wizard_step ? Number(row.wizard_step) : 0,
        wizard_group_key: row.wizard_group_key || "",
        items: [],
      });
    }

    lineGroups.get(lineKey).items.push(row);
  });

  lineGroups.forEach((group) => {
    const items = group.items
      .slice()
      .sort((a, b) => {
        const aDone = isImprovementCompletedRow(a) ? 1 : 0;
        const bDone = isImprovementCompletedRow(b) ? 1 : 0;
        return (
          aDone - bDone ||
          String(a.process_name || "").localeCompare(String(b.process_name || "")) ||
          Number(a.eval_id || 0) - Number(b.eval_id || 0)
        );
      });
    const completedCount = items.filter(isImprovementCompletedRow).length;
    const remainingCount = items.length - completedCount;
    if (remainingCount <= 0) return;

    const representative = items.find((row) => !isImprovementCompletedRow(row)) || items[0];
    directItems.push({
      ...representative,
      ...group,
      items,
      source_location_name: getLineWorkItemName(items, group.source_location_name),
      total_count: items.length,
      completed_count: completedCount,
      remaining_count: remainingCount,
      improve_status: remainingCount > 0 ? 10 : 20,
    });
  });

  return directItems;
};

const getWorkItemTitle = (row = {}) =>
  `${row.safe_machine_name || row.machine_name || "설비 미지정"} · ${
    row.process_name || "공정 미지정"
  }`;

const getWorkItemSubtitle = (row = {}) =>
  row.danger_description ||
  [row.danger_category, row.danger_cause].filter(Boolean).join(" / ") ||
  "유해·위험요인";

const isUploadableFile = (file) =>
  typeof File !== "undefined" && file instanceof File;

const resolveUploadFileUrl = (fileUrl) => {
  if (!fileUrl) return "";
  const url = String(fileUrl);
  if (/^(https?:)?\/\//.test(url) || url.startsWith("blob:") || url.startsWith("data:")) {
    return url;
  }
  if (url.startsWith("/backend/")) return url;
  return `${axiosInstance.defaults.baseURL || ""}${url}`;
};

const hasMeasureFileOrUrl = (file, url) =>
  Boolean(isUploadableFile(file) || url);

const isMeasureLocked = (hazard = {}, measure = {}) =>
  Number(hazard.improve_status || measure.improve_status || 0) >= 20 ||
  Boolean(hazard.improve_after && hazard.comp_date);

const hasSavedMeasurePlan = (hazard = {}, measure = {}) =>
  Boolean(
    String(measure.action || hazard.improve_plan || "").trim() &&
      (measure.emp_id || hazard.emp_id) &&
      (measure.dueDate || hazard.expected_date)
  );

const isMeasureStepLocked = (hazard = {}, measure = {}) =>
  Number(hazard.wizard_step || 0) >= 3 && hasSavedMeasurePlan(hazard, measure);

const isEvaluationLocked = (hazard = {}) =>
  Boolean(hazard.eval_id && hazard.frequency_id && hazard.importancy_id);

const isWizardContinuationRow = (row = {}) => {
  if (!row.eval_id) return false;

  const evalStatus = Number(row.eval_status || 0);
  const improveStatus = Number(row.improve_status || 0);
  const needsImprove =
    Number(row.requires_improve || 0) === 1 || Boolean(row.improve_id);
  const improveDone =
    improveStatus >= 20 || Boolean(row.improve_after && row.comp_date);

  if (evalStatus >= 20 && (!needsImprove || improveDone)) return false;
  return true;
};

const getDayDiff = (value) => {
  if (!value) return null;
  const today = new Date(`${getToday()}T00:00:00`);
  const target = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  return Math.round((target.getTime() - today.getTime()) / 86400000);
};

const getMeasureStatusMeta = (hazard = {}, measure = {}) => {
  const status = Number(measure.improve_status ?? hazard.improve_status ?? 0);
  const dueDate = measure.dueDate || hazard.expected_date;
  const action = String(measure.action || hazard.improve_plan || "").trim();
  const assignee = measure.emp_id || hazard.emp_id;
  const hasBefore = hasMeasureFileOrUrl(
    measure.beforeFile,
    measure.beforeUrl || hazard.improve_before
  );
  const hasAfter = hasMeasureFileOrUrl(
    measure.afterFile,
    measure.afterUrl || hazard.improve_after
  );
  const dayDiff = getDayDiff(dueDate);

  if (status >= 20 || hasAfter) {
    return {
      label: "개선 완료",
      color: "success",
      tone: "#16a34a",
      dayText: measure.compDate || hazard.comp_date || "",
    };
  }
  if (dayDiff !== null && dayDiff < 0) {
    return {
      label: `기한 초과 D+${Math.abs(dayDiff)}`,
      color: "error",
      tone: "#dc2626",
      dayText: dueDate,
    };
  }
  if (hasBefore) {
    return {
      label: dayDiff === null ? "개선 중" : `개선 중 D-${Math.max(dayDiff, 0)}`,
      color: "warning",
      tone: "#f59e0b",
      dayText: dueDate,
    };
  }
  if (action && assignee && dueDate) {
    return {
      label: dayDiff === null ? "대책 저장됨" : `대책 저장 D-${Math.max(dayDiff, 0)}`,
      color: "info",
      tone: "#2563eb",
      dayText: dueDate,
    };
  }
  return {
    label: "대책 작성 필요",
    color: "default",
    tone: "#64748b",
    dayText: dueDate,
  };
};

const getMeasureReasonChips = (hazard = {}, measure = {}) => {
  const dueDate = measure.dueDate || hazard.expected_date;
  const dayDiff = getDayDiff(dueDate);
  const hasAfter = hasMeasureFileOrUrl(
    measure.afterFile,
    measure.afterUrl || hazard.improve_after
  );
  const compDate = measure.compDate || hazard.comp_date;
  const action = measure.action || hazard.improve_plan;
  const assignee = measure.emp_id || hazard.emp_id;
  const status = Number(measure.improve_status ?? hazard.improve_status ?? 0);

  if (status >= 20 || (hasAfter && compDate)) {
    return [{ label: "완료 확인", color: "success" }];
  }

  const chips = [];
  if (!action) chips.push({ label: "대책 필요", color: "warning" });
  if (!assignee) chips.push({ label: "담당자 필요", color: "default" });
  if (!dueDate) chips.push({ label: "기한 필요", color: "default" });
  if (dayDiff !== null && dayDiff < 0) {
    chips.push({ label: `기한 초과 D+${Math.abs(dayDiff)}`, color: "error" });
  }
  if (!hasAfter) chips.push({ label: "개선 후 사진 필요", color: "warning" });
  if (!compDate) chips.push({ label: "완료일 필요", color: "default" });

  return chips.slice(0, 4);
};

const normalizeRiskCriteria = (data = {}) => ({
  frequencies: (data.frequencies || [])
    .map((item) => ({
      ...item,
      frequency_id: Number(item.frequency_id),
      frequency_level: Number(item.frequency_level),
    }))
    .filter(
      (item) =>
        Number.isFinite(item.frequency_id) &&
        Number.isFinite(item.frequency_level)
    )
    .sort(
      (a, b) =>
        a.frequency_level - b.frequency_level || a.frequency_id - b.frequency_id
    ),
  importancies: (data.importancies || [])
    .map((item) => ({
      ...item,
      importancy_id: Number(item.importancy_id),
      importancy_level: Number(item.importancy_level),
    }))
    .filter(
      (item) =>
        Number.isFinite(item.importancy_id) &&
        Number.isFinite(item.importancy_level)
    )
    .sort(
      (a, b) =>
        a.importancy_level - b.importancy_level ||
        a.importancy_id - b.importancy_id
    ),
  standards: (data.standards || [])
    .map((item) => ({
      ...item,
      standard_id: Number(item.standard_id),
      risk_level_min: Number(item.risk_level_min),
      risk_level_max: Number(item.risk_level_max),
      requires_improve: Number(item.requires_improve || 0),
    }))
    .filter(
      (item) =>
        Number.isFinite(item.standard_id) &&
        Number.isFinite(item.risk_level_min) &&
        Number.isFinite(item.risk_level_max)
    )
    .sort(
      (a, b) =>
        a.risk_level_min - b.risk_level_min ||
        a.risk_level_max - b.risk_level_max
    ),
});

const findClosestByLevel = (items, levelKey, targetLevel) => {
  if (!items.length) return null;
  const target = Number(targetLevel);
  return items.reduce((closest, item) => {
    if (!closest) return item;
    return Math.abs(Number(item[levelKey]) - target) <
      Math.abs(Number(closest[levelKey]) - target)
      ? item
      : closest;
  }, null);
};

const makeDefaultEvaluation = (
  criteria = EMPTY_RISK_CRITERIA,
  frequencyLevel = DEFAULT_RISK_CRITERIA.frequencies[2].frequency_level,
  importancyLevel = DEFAULT_RISK_CRITERIA.importancies[2].importancy_level
) => {
  const frequency = findClosestByLevel(
    criteria.frequencies,
    "frequency_level",
    frequencyLevel
  );
  const importancy = findClosestByLevel(
    criteria.importancies,
    "importancy_level",
    importancyLevel
  );

  return {
    frequency_id: frequency?.frequency_id || null,
    importancy_id: importancy?.importancy_id || null,
  };
};

const makeInitialEvaluations = () => ({});

const normalizeEvaluationForCriteria = (
  evaluation = {},
  criteria = EMPTY_RISK_CRITERIA
) => {
  const frequencyById = criteria.frequencies.find(
    (item) => Number(item.frequency_id) === Number(evaluation.frequency_id)
  );
  const importancyById = criteria.importancies.find(
    (item) => Number(item.importancy_id) === Number(evaluation.importancy_id)
  );

  if (frequencyById && importancyById) {
    return {
      frequency_id: frequencyById.frequency_id,
      importancy_id: importancyById.importancy_id,
    };
  }

  return makeDefaultEvaluation(
    criteria,
    evaluation.likelihood || 3,
    evaluation.severity || 3
  );
};

const findStandardByScore = (standards, score) =>
  standards.find(
    (standard) =>
      score >= standard.risk_level_min && score <= standard.risk_level_max
  );

const getRiskMeta = (score, standard) => {
  if (standard) {
    const requiresImprove = Number(standard.requires_improve) === 1;
    const high = score >= 13;
    const medium = requiresImprove || score >= 8;
    return {
      level:
        standard.manage_criteria || (high ? "높음" : medium ? "보통" : "낮음"),
      color: high ? "error" : medium ? "warning" : "success",
      hex: high ? "#dc2626" : medium ? "#f59e0b" : "#16a34a",
      bg: high ? "#fef2f2" : medium ? "#fffbeb" : "#f0fdf4",
      message: requiresImprove ? "개선 필요" : "허용 가능",
      requiresImprove,
      standardId: standard.standard_id,
    };
  }

  if (score >= 15) {
    return {
      level: "높음",
      color: "error",
      hex: "#dc2626",
      bg: "#fef2f2",
      message: "즉시 개선 필요",
    };
  }
  if (score >= 8) {
    return {
      level: "보통",
      color: "warning",
      hex: "#f59e0b",
      bg: "#fffbeb",
      message: "개선 계획 수립",
    };
  }
  return {
    level: "낮음",
    color: "success",
    hex: "#16a34a",
    bg: "#f0fdf4",
    message: "현 상태 유지",
  };
};

const getEvaluationDetail = (criteria, evaluation = {}) => {
  const normalized = normalizeEvaluationForCriteria(evaluation, criteria);
  const frequency = criteria.frequencies.find(
    (item) => Number(item.frequency_id) === Number(normalized.frequency_id)
  );
  const importancy = criteria.importancies.find(
    (item) => Number(item.importancy_id) === Number(normalized.importancy_id)
  );
  const frequencyLevel = Number(frequency?.frequency_level || 0);
  const importancyLevel = Number(importancy?.importancy_level || 0);
  const score = frequencyLevel * importancyLevel;
  const standard = findStandardByScore(criteria.standards, score);

  return {
    ...normalized,
    frequency,
    importancy,
    frequencyLevel,
    importancyLevel,
    score,
    standard,
    riskMeta: getRiskMeta(score, standard),
  };
};

const makeMeasureDraft = (hazard) => ({
  action: hazard?.improve_plan || "",
  emp_id: hazard?.emp_id ? String(hazard.emp_id) : "",
  dueDate: hazard?.expected_date || getDefaultDueDate(),
  beforeFile: null,
  afterFile: null,
  beforeUrl: hazard?.improve_before || "",
  afterUrl: hazard?.improve_after || "",
  afterRisk: hazard?.improve_after_risk
    ? String(hazard.improve_after_risk)
    : "",
  compDate: hazard?.comp_date || "",
  improve_status: Number(hazard?.improve_status || 0),
});

function StepNavigation({ activeStep, progress, onStepClick }) {
  return (
    <Box sx={{ pt: 1 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(4, minmax(154px, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1,
          overflowX: "auto",
          pb: 0.5,
        }}
      >
        {STEPS.map((step, index) => {
          const active = activeStep === index;
          const done = activeStep > index;
          return (
            <Box
              key={step.label}
              component="button"
              type="button"
              onClick={() => onStepClick(index)}
              sx={{
                width: "100%",
                p: 1.25,
                border: "1px solid",
                borderColor: active
                  ? "#2563eb"
                  : done
                  ? "#86efac"
                  : "rgba(148, 163, 184, 0.3)",
                borderRadius: "10px",
                bgcolor: active ? "#eff6ff" : done ? "#f0fdf4" : "#fff",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    color: active ? "#2563eb" : done ? "#16a34a" : "#64748b",
                    bgcolor: active ? "#dbeafe" : done ? "#dcfce7" : "#f1f5f9",
                  }}
                >
                  {done ? <CheckCircleIcon fontSize="small" /> : step.icon}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    STEP {index + 1}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                    {step.label}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          mt: 1.25,
          height: 8,
          borderRadius: 999,
          bgcolor: "#e2e8f0",
          "& .MuiLinearProgress-bar": {
            borderRadius: 999,
            bgcolor: progress === 100 ? "#16a34a" : "#2563eb",
          },
        }}
      />
    </Box>
  );
}

function WizardHeader({ activeStep, progress, onStepClick, focusRequest }) {
  const isLineAssessment =
    focusRequest?.assessmentScope === "line" || Boolean(focusRequest?.locationId);

  return (
    <Paper elevation={0} sx={{ ...shellSx, p: { xs: 2, md: 2.5 } }}>
      <Stack spacing={2.25}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{ width: 48, height: 48, bgcolor: "#0f172a", color: "#fff" }}
            >
              <SecurityIcon />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 900, letterSpacing: 0 }}
              >
                위험성평가 Wizard
              </Typography>
              {isLineAssessment ? (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    label="라인 평가"
                    color="primary"
                    sx={{ borderRadius: "7px", fontWeight: 900 }}
                  />
                  {focusRequest?.locationName ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={focusRequest.locationName}
                      sx={{ borderRadius: "7px", fontWeight: 800, maxWidth: 220 }}
                    />
                  ) : null}
                </Stack>
              ) : null}
            </Box>
          </Stack>
        </Stack>
        <StepNavigation
          activeStep={activeStep}
          progress={progress}
          onStepClick={onStepClick}
        />
      </Stack>
    </Paper>
  );
}

function SectionTitle({ icon, title, description, action }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="space-between"
      sx={{ mb: 2.25 }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Avatar sx={{ bgcolor: "#eff6ff", color: "#2563eb" }}>{icon}</Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Stack>
      {action}
    </Stack>
  );
}

function AddHazardDialog({ open, groups, onClose, onAdd }) {
  const [groupId, setGroupId] = useState(groups[0]?.id || "");
  const [title, setTitle] = useState("");
  const [source, setSource] = useState("현장 추가");

  useEffect(() => {
    if (open) {
      setGroupId(groups[0]?.id || "");
      setTitle("");
      setSource("현장 추가");
    }
  }, [groups, open]);

  const handleSubmit = () => {
    if (!groupId) {
      toast.warning("설비·공정 대상을 선택해주세요.");
      return;
    }
    if (!title.trim()) {
      toast.warning("추가할 위험요인을 입력해주세요.");
      return;
    }
    onAdd({
      groupId,
      title: title.trim(),
      source: source.trim() || "현장 추가",
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            위험요인 추가
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f7f8fb" }}>
        <Stack spacing={2}>
          <TextField
            select
            label="설비·공정"
            value={groupId}
            onChange={(event) => setGroupId(event.target.value)}
            fullWidth
          >
            {groups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.equipment} ({group.process})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="위험요인"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            fullWidth
            multiline
            minRows={3}
            placeholder="예: 작업자 이동 동선과 지게차 동선 충돌 위험"
          />
          <TextField
            label="분류/출처"
            value={source}
            onChange={(event) => setSource(event.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color="inherit" onClick={onClose}>
          취소
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleSubmit}
        >
          추가
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function WorkItemCard({ item, type, onSelect }) {
  const workRows =
    Array.isArray(item.items) && item.items.length ? item.items : [item];
  const isLineGroup = Boolean(item.isLineWorkItemGroup);
  const representative =
    workRows.find((row) => !isImprovementCompletedRow(row)) || workRows[0] || item;
  const completedCount = workRows.filter(isImprovementCompletedRow).length;
  const remainingCount = Math.max(workRows.length - completedCount, 0);
  const improveStatus = Number(item.improve_status || 0);
  const statusLabel = isLineGroup
    ? `남은 ${remainingCount}개`
    : improveStatus >= 10
    ? "개선 중"
    : "대책 작성";
  const reasonChips = isLineGroup
    ? []
    : getMeasureReasonChips(item, makeMeasureDraft(item));
  const title = isLineGroup
    ? item.source_location_name || "라인 평가"
    : getWorkItemTitle(item);
  const subtitle = isLineGroup
    ? `${workRows.length}개 위험요인 · 완료 ${completedCount}개 · 남은 ${remainingCount}개`
    : getWorkItemSubtitle(item);

  return (
    <Box
      sx={{
        ...panelSx,
        p: 1.75,
        borderColor: isLineGroup ? "rgba(37, 99, 235, 0.28)" : panelSx.borderColor,
        bgcolor: isLineGroup ? "#f8fbff" : panelSx.bgcolor,
      }}
    >
      <Stack spacing={1.25}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              {isLineGroup ? (
                <Chip
                  size="small"
                  label="라인"
                  sx={{
                    height: 22,
                    fontWeight: 900,
                    color: "#1d4ed8",
                    bgcolor: "#dbeafe",
                    border: "1px solid #bfdbfe",
                  }}
                />
              ) : null}
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 900, color: "#0f172a" }}
                noWrap
              >
                {title}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {subtitle}
            </Typography>
          </Box>
          <Chip
            size="small"
            color="info"
            label={statusLabel}
            sx={{ flexShrink: 0, fontWeight: 800 }}
          />
        </Stack>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {representative.eval_date ? (
            <Chip
              size="small"
              variant="outlined"
              label={`평가 ${representative.eval_date}`}
            />
          ) : null}
          {representative.expected_date ? (
            <Chip
              size="small"
              variant="outlined"
              label={`예정 ${representative.expected_date}`}
            />
          ) : null}
          {isLineGroup ? (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={`완료 ${completedCount}/${workRows.length}`}
            />
          ) : null}
          {item.manage_criteria ? (
            <Chip
              size="small"
              variant="outlined"
              label={item.manage_criteria}
            />
          ) : null}
          {reasonChips.map((chip) => (
            <Chip
              key={chip.label}
              size="small"
              color={chip.color}
              variant={chip.color === "default" ? "outlined" : "filled"}
              label={chip.label}
            />
          ))}
        </Stack>

        <Stack direction="row" justifyContent="flex-end">
          <Button
            size="small"
            variant="contained"
            onClick={() => onSelect(item, type)}
          >
            개선 이어가기
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

const WIZARD_ENTRY_AUTOPILOT_STEPS = [
  {
    target: '[data-autopilot="wizard-entry-summary"]',
    title: "진행 상태 확인",
    description:
      "작성 중, 개선 중 항목을 먼저 확인합니다. 새로 시작하기 전에 이어서 처리할 평가가 있는지 한눈에 보는 영역입니다.",
  },
  {
    target: '[data-autopilot="wizard-drafts-list"]',
    title: "작성 중 항목 이어서 작성",
    description:
      "2단계 이후 자동 저장된 평가는 여기에서 다시 열 수 있습니다. 작성 중 항목은 DB에 제출된 평가가 아니라 브라우저 임시 저장 기록입니다.",
  },
  {
    target: '[data-autopilot="wizard-improvement-list"]',
    title: "개선 중 항목 마무리",
    description:
      "개선대책이 필요한 항목은 개선 중으로 남습니다. 개선 후 사진과 완료일을 추가하면 한 사이클을 마무리할 수 있습니다.",
  },
  {
    target: '[data-autopilot="wizard-new-start"]',
    title: "새 평가 시작",
    description:
      "새 평가는 이번 달 아직 평가하지 않은 설비별 위험요인을 선택하고, 같은 화면에서 위험성평가를 입력한 뒤 감소대책과 확인/승인 순서로 진행됩니다.",
  },
];

function DraftPickerDialog({
  open,
  drafts,
  improvementItems,
  pendingCount = 0,
  workItemsLoading,
  workItemsError,
  onSelect,
  onNew,
  onDelete,
  onSelectWorkItem,
}) {
  const [autoPilotOpen, setAutoPilotOpen] = useState(false);
  const [autoPilotStep, setAutoPilotStep] = useState(0);
  const activeDrafts = drafts.filter(
    (draft) => !draft.submitted && !isExistingWorkItemDraft(draft)
  );

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      hideBackdrop
      disableEnforceFocus
      disableScrollLock
      sx={{ pointerEvents: "none" }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
          width: { xs: "calc(100% - 20px)", md: 1080 },
          maxHeight: "86vh",
          boxShadow: "0 24px 80px rgba(15, 23, 42, 0.32)",
          pointerEvents: "auto",
        },
      }}
    >
      <Box sx={{ bgcolor: "#f8fafc" }}>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2.25,
            bgcolor: "#fff",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                위험성평가 Wizard
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<PlayCircleOutlineIcon />}
                onClick={() => {
                  setAutoPilotStep(0);
                  setAutoPilotOpen(true);
                }}
              >
                오토파일럿
              </Button>
              <Button
                data-autopilot="wizard-new-start"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onNew}
              >
                새 평가 시작
              </Button>
            </Stack>
          </Stack>
        </Box>

        {workItemsLoading ? <LinearProgress /> : null}

        <Box
          sx={{
            px: { xs: 1.5, md: 3 },
            py: 2.5,
            maxHeight: "calc(86vh - 116px)",
            overflow: "auto",
          }}
        >
          <Grid container spacing={1.5} sx={{ mb: 2 }} data-autopilot="wizard-entry-summary">
            {[
              {
                label: "미평가 항목",
                value: pendingCount,
                helper: "이번 달 평가 대상",
                icon: <RuleIcon />,
                color: "#2563eb",
              },
              {
                label: "작성 중",
                value: activeDrafts.length,
                helper: "자동 저장",
                icon: <PlaylistAddCheckIcon />,
                color: "#0f766e",
              },
              {
                label: "개선 중",
                value: improvementItems.length,
                helper: "완료 처리 필요",
                icon: <BuildCircleIcon />,
                color: "#ea580c",
              },
            ].map((item) => (
              <Grid item xs={6} md={4} key={item.label}>
                <Box
                  sx={{
                    ...panelSx,
                    p: 1.5,
                    minHeight: 96,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <Stack
                    spacing={0.75}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        bgcolor: `${item.color}14`,
                        color: item.color,
                      }}
                    >
                      {item.icon}
                    </Avatar>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 800, lineHeight: 1 }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 900, color: item.color, lineHeight: 1 }}
                    >
                      {item.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 700, lineHeight: 1 }}
                    >
                      {item.helper}
                    </Typography>
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>

          {workItemsError ? (
            <Alert severity="warning" sx={{ mb: 1.5, borderRadius: "8px" }}>
              진행 중인 항목을 불러오지 못했습니다. 작성 중인 평가나 새 평가는
              그대로 사용할 수 있습니다.
            </Alert>
          ) : null}

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <Box
                data-autopilot="wizard-drafts-list"
                sx={{
                  ...panelSx,
                  p: 1.5,
                  height: { xs: "auto", md: 360 },
                  maxHeight: 360,
                  overflow: "auto",
                }}
              >
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ bgcolor: "#eff6ff", color: "#2563eb" }}>
                      <PlaylistAddCheckIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                        작성 중
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        자동 저장된 평가
                      </Typography>
                    </Box>
                  </Stack>

                  {activeDrafts.length ? (
                    activeDrafts.map((draft) => (
                      <Box
                        key={draft.id}
                        sx={{
                          p: 1.5,
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          bgcolor: "#fff",
                        }}
                      >
                        <Stack spacing={1.2}>
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 900 }}
                                noWrap
                              >
                                {draft.title || "위험성평가 Wizard"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                마지막 저장 {formatDraftTime(draft.updatedAt)}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              color="primary"
                              label={`STEP ${(draft.activeStep || 0) + 1}`}
                            />
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`위험요인 ${
                                draft.selectedHazardIds?.length || 0
                              }개`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`개선대책 ${
                                Object.keys(draft.measures || {}).length
                              }개`}
                            />
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => onDelete(draft.id)}
                            >
                              삭제
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => onSelect(draft)}
                            >
                              이어서 작성
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    ))
                  ) : (
                    <EmptyState
                      compact
                      title="작성 중인 평가가 없습니다"
                      description="2단계부터 자동 저장됩니다."
                    />
                  )}
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                data-autopilot="wizard-improvement-list"
                sx={{
                  ...panelSx,
                  p: 1.5,
                  height: { xs: "auto", md: 360 },
                  maxHeight: 360,
                  overflow: "auto",
                }}
              >
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ bgcolor: "#e0f2fe", color: "#0284c7" }}>
                      <BuildCircleIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                        개선 중
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        개선 후 사진과 완료일 등록
                      </Typography>
                    </Box>
                  </Stack>

                  {improvementItems.length ? (
                    improvementItems.map((item) => (
                      <WorkItemCard
                        key={`improvement-${item.id || item.eval_id}`}
                        item={item}
                        type="improvement"
                        onSelect={onSelectWorkItem}
                      />
                    ))
                  ) : (
                    <EmptyState
                      compact
                      title="개선 중인 항목이 없습니다"
                      description="개선할 항목이 있으면 이곳에 표시됩니다."
                    />
                  )}

                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "8px",
                      bgcolor: "#f8fafc",
                      border: "1px dashed #cbd5e1",
                    }}
                  >
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                        새 평가
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {pendingCount > 0
                          ? `이번 달 미평가 항목 ${pendingCount}건으로 시작합니다.`
                          : "이번 달 미평가 항목이 없으면 목록이 비어 있습니다."}
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={onNew}
                      >
                        새 평가 시작
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <AutoPilotGuide
        open={autoPilotOpen}
        steps={WIZARD_ENTRY_AUTOPILOT_STEPS}
        stepIndex={autoPilotStep}
        onStepChange={setAutoPilotStep}
        onClose={() => setAutoPilotOpen(false)}
      />
    </Dialog>
  );
}

function HazardConnectionStep({
  groups,
  improvementGroups,
  selectedHazardIds,
  onToggleHazard,
  onToggleHazardSet,
  onAddOpen,
  pendingLoading,
  pendingError,
  measures = {},
}) {
  const [scopeFilter, setScopeFilter] = useState("all");
  const selectedCount = selectedHazardIds.size;
  const displayGroups = [...(improvementGroups || []), ...(groups || [])];
  const lineDisplayGroups = createLineEvaluationGroups(displayGroups);
  const filteredDisplayGroups = getEvaluationGroupsByScope(
    displayGroups,
    scopeFilter
  );
  const scopeCounts = {
    all: displayGroups.length + lineDisplayGroups.length,
    asset: displayGroups.length,
    line: lineDisplayGroups.length,
  };
  const improvementCount = countHazardsInGroups(improvementGroups || []);
  const pendingCount = countHazardsInGroups(groups || []);
  const locationSummaryItems = Array.from(
    new Set(
      filteredDisplayGroups
        .flatMap((group) =>
          Array.isArray(group.location_names) && group.location_names.length
            ? group.location_names
            : group.location_summary
            ? [group.location_summary]
            : []
        )
        .filter(Boolean)
    )
  );
  const equipmentSummaryItems = Array.from(
    new Set(filteredDisplayGroups.map((group) => group.equipment).filter(Boolean))
  );

  return (
    <Box>
      <SectionTitle
        icon={<PlaylistAddCheckIcon />}
        title="평가할 항목을 선택해주세요"
        description="새 평가와 이어서 할 개선 작업을 한곳에서 선택합니다."
        action={
          <Button
            sx={{ display: "none" }}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddOpen}
          >
            위험요인 추가
          </Button>
        }
      />

      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{ mt: -1.25, mb: 1.75 }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={scopeFilter}
          onChange={(_, nextValue) => {
            if (nextValue) setScopeFilter(nextValue);
          }}
          sx={{
            p: 0.35,
            bgcolor: "#f8fafc",
            border: "1px solid rgba(148, 163, 184, 0.28)",
            borderRadius: "10px",
            "& .MuiToggleButton-root": {
              minWidth: { xs: 72, sm: 78 },
              px: 1.3,
              py: 0.65,
              border: 0,
              borderRadius: "8px !important",
              color: "#64748b",
              fontWeight: 900,
              "&.Mui-selected": {
                color: "#2563eb",
                bgcolor: "#ffffff",
                boxShadow: "0 6px 18px rgba(37, 99, 235, 0.14)",
              },
              "&.Mui-selected:hover": {
                bgcolor: "#ffffff",
              },
            },
          }}
        >
          {[
            { value: "all", label: "전체", count: scopeCounts.all },
            { value: "asset", label: "설비", count: scopeCounts.asset },
            { value: "line", label: "라인", count: scopeCounts.line },
          ].map((item) => (
            <ToggleButton key={item.value} value={item.value}>
              {item.label} {item.count}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {pendingLoading ? (
        <Alert severity="info" sx={{ borderRadius: "8px", mb: 2 }}>
          이번 달 미평가 유해·위험요인을 불러오는 중입니다.
        </Alert>
      ) : null}
      {pendingError ? (
        <Alert severity="error" sx={{ borderRadius: "8px", mb: 2 }}>
          미평가 유해·위험요인 조회에 실패했습니다. 서버 상태를 확인해주세요.
        </Alert>
      ) : null}
      {!pendingLoading && !pendingError && displayGroups.length === 0 ? (
        <Box sx={{ mb: 2 }}>
          <EmptyState
            compact
            icon={<CheckCircleIcon />}
            title="이번 달 진행할 항목이 없습니다"
            description="이번 달에 새로 평가하거나 이어서 처리할 항목이 없습니다."
          />
        </Box>
      ) : null}
      {!pendingLoading &&
      !pendingError &&
      displayGroups.length > 0 &&
      filteredDisplayGroups.length === 0 ? (
        <Box sx={{ mb: 2 }}>
          <EmptyState
            compact
            icon={<PlaylistAddCheckIcon />}
            title="선택한 조건의 평가 항목이 없습니다"
            description="전체, 설비, 라인 필터를 변경해 다른 평가 항목을 확인할 수 있습니다."
          />
        </Box>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Stack spacing={1.5}>
            {filteredDisplayGroups.map((group) => {
              const groupHazardIds = group.hazards.map((hazard) => hazard.id);
              const checkedCount = groupHazardIds.filter((id) =>
                selectedHazardIds.has(id)
              ).length;
              const allChecked =
                checkedCount === groupHazardIds.length &&
                groupHazardIds.length > 0;
              const isImprovementGroup = group.workflowType === "improvement";
              const isProgressGroup = group.workflowType === "progress";
              const improvementHazards = isImprovementGroup
                ? group.hazards || []
                : [];
              const completedImprovementCount = improvementHazards.filter(
                (hazard) =>
                  getMeasureStatusMeta(
                    hazard,
                    measures?.[hazard.id] || makeMeasureDraft(hazard)
                  ).label === "개선 완료"
              ).length;
              const isCompletedImprovementGroup =
                isImprovementGroup &&
                improvementHazards.length > 0 &&
                completedImprovementCount === improvementHazards.length;
              return (
                <Box
                  key={group.id}
                  sx={{
                    ...panelSx,
                    overflow: "hidden",
                    borderWidth: group.scopeType === "line" ? 1.5 : 1.25,
                    borderColor: checkedCount
                      ? "#93c5fd"
                      : group.scopeType === "line"
                      ? "#bfdbfe"
                      : "#d8e0ec",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                    sx={{
                      px: 1.5,
                      py: 1.35,
                      bgcolor: checkedCount
                        ? "#eff6ff"
                        : group.scopeType === "line"
                        ? "#f8fbff"
                        : "#f8fafc",
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Checkbox
                        checked={allChecked}
                        indeterminate={checkedCount > 0 && !allChecked}
                        onChange={() => onToggleHazardSet(groupHazardIds)}
                      />
                      <Box>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          {group.scopeType === "line" ? (
                            <Chip
                              size="small"
                              label="라인"
                              sx={{
                                height: 22,
                                borderRadius: "6px",
                                bgcolor: "#eef6ff",
                                color: "#2563eb",
                                border: "1px solid #bfdbfe",
                                fontWeight: 900,
                              }}
                            />
                          ) : null}
                        <Typography sx={{ fontWeight: 900 }} noWrap>
                          {group.scopeType === "line" ? (
                            group.location_summary || group.equipment
                          ) : (
                            <>
                          {group.equipment} · {group.process}
                            </>
                          )}
                        </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {group.line}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                      justifyContent="flex-end"
                    >
                      {group.location_summary ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="info"
                          label={group.location_summary}
                        />
                      ) : null}
                      <Chip
                        size="small"
                        color={
                          isCompletedImprovementGroup
                            ? "success"
                            : isImprovementGroup
                            ? "warning"
                            : isProgressGroup
                            ? "info"
                            : "primary"
                        }
                        variant={
                          isImprovementGroup || isProgressGroup
                            ? "filled"
                            : "outlined"
                        }
                        label={
                          isCompletedImprovementGroup
                            ? "개선 완료"
                            : isImprovementGroup
                            ? "개선 중"
                            : isProgressGroup
                            ? "진행 중"
                            : "미평가"
                        }
                      />
                      <Chip
                        size="small"
                        color={checkedCount ? "primary" : "default"}
                        label={`${checkedCount}/${group.hazards.length} 선택`}
                      />
                    </Stack>
                  </Stack>
                  <Stack spacing={1} sx={{ p: 1.25 }}>
                    {group.hazards.map((hazard) => {
                      const checked = selectedHazardIds.has(hazard.id);
                      const measureDraft =
                        measures?.[hazard.id] || makeMeasureDraft(hazard);
                      const statusMeta = getMeasureStatusMeta(
                        hazard,
                        measureDraft
                      );
                      const progressChipMeta =
                        hazard.workflowType === "progress"
                          ? getProgressEvaluationChipMeta(hazard, measureDraft)
                          : null;
                      const reasonChips =
                        hazard.workflowType === "improvement"
                          ? getMeasureReasonChips(
                              hazard,
                              measureDraft
                            )
                          : [];
                      return (
                        <Box
                          key={hazard.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => onToggleHazard(hazard.id)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ")
                              onToggleHazard(hazard.id);
                          }}
                          sx={{
                            p: 1.2,
                            border: "1px solid",
                            borderColor: checked
                              ? "#2563eb"
                              : "rgba(148, 163, 184, 0.26)",
                            borderRadius: "8px",
                            bgcolor: checked ? "#fff" : "#fbfdff",
                            cursor: "pointer",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.2}
                            alignItems="center"
                          >
                            <Checkbox
                              checked={checked}
                              tabIndex={-1}
                              disableRipple
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 900 }}
                              >
                                {hazard.title}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                              >
                                {hazard.worker_proposal_source ? (
                                  <Chip
                                    size="small"
                                    color="warning"
                                    icon={<PersonSearchIcon />}
                                    label={
                                      hazard.latest_worker_proposal_id
                                        ? `근로자 제안 #${hazard.latest_worker_proposal_id}`
                                        : "근로자 제안"
                                    }
                                    sx={{
                                      height: 22,
                                      "& .MuiChip-icon": { fontSize: 15 },
                                    }}
                                  />
                                ) : null}
                                {hazard.workflowType === "improvement" ? (
                                  <Chip
                                    size="small"
                                    color={statusMeta.color}
                                    label={statusMeta.label}
                                    sx={{ height: 22, fontWeight: 800 }}
                                  />
                                ) : null}
                                {progressChipMeta ? (
                                  <Chip
                                    size="small"
                                    color={progressChipMeta.color}
                                    variant={progressChipMeta.variant}
                                    label={progressChipMeta.label}
                                    sx={{ height: 22, fontWeight: 800 }}
                                  />
                                ) : null}
                                {reasonChips.map((chip) => (
                                  <Chip
                                    key={`${hazard.id}-${chip.label}`}
                                    size="small"
                                    color={chip.color}
                                    variant={
                                      chip.color === "default"
                                        ? "outlined"
                                        : "filled"
                                    }
                                    label={chip.label}
                                    sx={{ height: 22, fontWeight: 800 }}
                                  />
                                ))}
                                {Number(hazard.duplicate_count || 1) > 1 ? (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`중복 ${hazard.duplicate_count}건 정리`}
                                    sx={{ height: 22, fontWeight: 800 }}
                                  />
                                ) : null}
                                {group.process_count > 1 ? (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={hazard.process || "공정 미지정"}
                                    sx={{ height: 22, fontWeight: 800 }}
                                  />
                                ) : null}
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {hazard.source}
                                </Typography>
                              </Stack>
                            </Box>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack
            spacing={1.5}
            sx={{ position: { md: "sticky" }, top: { md: 16 } }}
          >
            <Box sx={{ ...panelSx, p: 2 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.75 }}>
                라인·설비 기준
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  label={`라인 ${locationSummaryItems.length || 0}개`}
                />
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`설비 ${equipmentSummaryItems.length || 0}개`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`위험요인 ${filteredDisplayGroups.reduce(
                    (sum, group) => sum + (group.hazards?.length || 0),
                    0
                  )}건`}
                />
              </Stack>
              <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                {locationSummaryItems.slice(0, 4).map((name) => (
                  <Typography key={name} variant="body2" color="text.secondary">
                    {name}
                  </Typography>
                ))}
                {locationSummaryItems.length > 4 ? (
                  <Typography variant="caption" color="text.secondary">
                    외 {locationSummaryItems.length - 4}개 라인
                  </Typography>
                ) : null}
                {!locationSummaryItems.length ? (
                  <Typography variant="body2" color="text.secondary">
                    연결된 라인 정보가 없는 항목입니다.
                  </Typography>
                ) : null}
              </Stack>
            </Box>
            <Box sx={{ ...panelSx, p: 2 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.75 }}>
                연결 요약
              </Typography>
              <Typography variant="body2" color="text.secondary">
                선택한 위험요인은 아래에서 바로 가능성과 중대성을 평가합니다.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Chip color="primary" label={`${selectedCount}건 선택`} />
                <Chip variant="outlined" label={`미평가 ${pendingCount}건`} />
                <Chip
                  variant="outlined"
                  color={improvementCount ? "warning" : "default"}
                  label={`개선 중 ${improvementCount}건`}
                />
              </Stack>
            </Box>
            <Alert severity="info" sx={{ borderRadius: "8px" }}>
              개선대책을 저장하면 개선 중으로 남고, 개선 후 사진과 완료일을
              넣으면 개선 완료로 처리됩니다.
            </Alert>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

function RiskEvaluationStep({
  selectedHazards,
  evaluations,
  riskCriteria,
  criteriaLoading,
  criteriaError,
  currentHazardIndex,
  onCurrentHazardIndexChange,
  onEvaluationChange,
}) {
  const currentHazard = selectedHazards[currentHazardIndex];
  const currentEvaluation = currentHazard
    ? normalizeEvaluationForCriteria(
        evaluations[currentHazard.id],
        riskCriteria
      )
    : null;
  const currentDetail = currentEvaluation
    ? getEvaluationDetail(riskCriteria, currentEvaluation)
    : null;
  const score = currentDetail?.score || 0;
  const riskMeta = currentDetail?.riskMeta || getRiskMeta(0);
  const hasRiskCriteria =
    riskCriteria.frequencies.length > 0 &&
    riskCriteria.importancies.length > 0 &&
    riskCriteria.standards.length > 0;

  if (!currentHazard)
    return <Alert severity="warning">먼저 위험요인을 선택해주세요.</Alert>;

  const currentEvaluationLocked = isEvaluationLocked(currentHazard);

  const updateValue = (key, value) => {
    if (currentEvaluationLocked) return;
    if (!value) return;
    onEvaluationChange(currentHazard.id, key, Number(value));
  };

  const title = (
    <SectionTitle
      icon={<TuneIcon />}
      title="위험도 평가"
      description="가능성과 중대성을 선택하면 위험도와 조치 기준이 계산됩니다."
    />
  );

  if (criteriaLoading) {
    return (
      <Box>
        {title}
        <Alert severity="info" sx={{ borderRadius: "8px" }}>
          위험성 평가 기준값을 데이터베이스에서 불러오는 중입니다.
        </Alert>
      </Box>
    );
  }

  if (!hasRiskCriteria) {
    return (
      <Box>
        {title}
        <Alert
          severity={criteriaError ? "error" : "warning"}
          sx={{ borderRadius: "8px" }}
        >
          가능성, 중대성, 허용 기준 데이터가 없습니다. safe_cal_frequency,
          safe_cal_importancy, safe_cal_standard 데이터를 확인해주세요.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <SectionTitle
        icon={<TuneIcon />}
        title="위험도 평가"
        description="가능성과 중대성을 선택하면 위험도와 조치 기준이 계산됩니다."
      />

      <Stack spacing={1.5}>
        {criteriaLoading ? (
          <Alert severity="info" sx={{ borderRadius: "8px" }}>
            위험성 평가 기준표를 불러오는 중입니다.
          </Alert>
        ) : null}
        {criteriaError ? (
          <Alert severity="warning" sx={{ borderRadius: "8px" }}>
            기준표 API 조회에 실패하여 기본 기준으로 표시합니다.
          </Alert>
        ) : null}
      </Stack>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={4}>
          <Stack spacing={1}>
            {selectedHazards.map((hazard, index) => {
              const itemDetail = getEvaluationDetail(
                riskCriteria,
                evaluations[hazard.id]
              );
              const active = index === currentHazardIndex;
              const locked = isEvaluationLocked(hazard);
              return (
                <Box
                  key={hazard.id}
                  component="button"
                  type="button"
                  disabled={locked}
                  onClick={() => onCurrentHazardIndexChange(index)}
                  sx={{
                    width: "100%",
                    p: 1.25,
                    border: "1px solid",
                    borderColor: active
                      ? "#2563eb"
                      : "rgba(148, 163, 184, 0.28)",
                    borderRadius: "8px",
                    bgcolor: active ? "#eff6ff" : "#fff",
                    cursor: locked ? "not-allowed" : "pointer",
                    opacity: locked ? 0.72 : 1,
                    textAlign: "left",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 900 }}
                        noWrap
                      >
                        {hazard.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {hazard.equipment}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={locked ? "success" : itemDetail.riskMeta.color}
                      variant={locked ? "outlined" : "filled"}
                      label={locked ? "이번 달 평가완료" : itemDetail.score}
                    />
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Grid>

        <Grid item xs={12} md={8}>
          <Stack spacing={1.5}>
            <Box sx={{ ...panelSx, p: 2 }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`설비: ${currentHazard.equipment}`} />
                  <Chip label={`공정: ${currentHazard.process}`} />
                  <Chip
                    color="primary"
                    label={`${currentHazardIndex + 1}/${
                      selectedHazards.length
                    }`}
                  />
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {currentHazard.title}
                </Typography>
              </Stack>
            </Box>

            <Grid container spacing={1.5}>
              {currentEvaluationLocked ? (
                <Grid item xs={12}>
                  <Alert severity="success" sx={{ borderRadius: "8px" }}>
                    이미 이번 달 위험성평가가 저장된 항목입니다. 저장된
                    값은 읽기 전용으로 표시되며, 다음 단계에서 감소대책만
                    이어서 입력합니다.
                  </Alert>
                </Grid>
              ) : null}
              <Grid item xs={12} sm={6}>
                <Box sx={{ ...panelSx, p: 2 }}>
                  <Typography sx={{ fontWeight: 900, mb: 1 }}>
                    가능성 (빈도)
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={currentEvaluation.frequency_id}
                    onChange={(_, value) => updateValue("frequency_id", value)}
                    disabled={currentEvaluationLocked}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(2, minmax(0, 1fr))",
                        sm: `repeat(${Math.max(
                          1,
                          riskCriteria.frequencies.length
                        )}, minmax(0, 1fr))`,
                      },
                      gap: 0.75,
                      "& .MuiToggleButton-root": {
                        py: 1,
                        px: 0.75,
                        minWidth: 0,
                        border:
                          "1px solid rgba(148, 163, 184, 0.36) !important",
                        borderRadius: "8px !important",
                        fontWeight: 900,
                      },
                    }}
                  >
                    {riskCriteria.frequencies.map((item) => (
                      <ToggleButton
                        key={item.frequency_id}
                        value={item.frequency_id}
                        disabled={currentEvaluationLocked}
                      >
                        <Stack
                          spacing={0.2}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Typography sx={{ fontWeight: 900, lineHeight: 1 }}>
                            {item.frequency_level}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: "100%" }}
                          >
                            {item.frequency_description}
                          </Typography>
                        </Stack>
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 1 }}
                  >
                    선택:{" "}
                    {currentDetail.frequency?.frequency_description || "-"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ ...panelSx, p: 2 }}>
                  <Typography sx={{ fontWeight: 900, mb: 1 }}>
                    중대성 (강도)
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={currentEvaluation.importancy_id}
                    onChange={(_, value) => updateValue("importancy_id", value)}
                    disabled={currentEvaluationLocked}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(2, minmax(0, 1fr))",
                        sm: `repeat(${Math.max(
                          1,
                          riskCriteria.importancies.length
                        )}, minmax(0, 1fr))`,
                      },
                      gap: 0.75,
                      "& .MuiToggleButton-root": {
                        py: 1,
                        px: 0.75,
                        minWidth: 0,
                        border:
                          "1px solid rgba(148, 163, 184, 0.36) !important",
                        borderRadius: "8px !important",
                        fontWeight: 900,
                      },
                    }}
                  >
                    {riskCriteria.importancies.map((item) => (
                      <ToggleButton
                        key={item.importancy_id}
                        value={item.importancy_id}
                        disabled={currentEvaluationLocked}
                      >
                        <Stack
                          spacing={0.2}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Typography sx={{ fontWeight: 900, lineHeight: 1 }}>
                            {item.importancy_level}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: "100%" }}
                          >
                            {item.importancy_description}
                          </Typography>
                        </Stack>
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 1 }}
                  >
                    선택:{" "}
                    {currentDetail.importancy?.importancy_description || "-"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: riskMeta.hex,
                borderRadius: "10px",
                bgcolor: riskMeta.bg,
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography sx={{ fontWeight: 900 }}>
                    위험도 산출 결과
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    가능성 {currentDetail.frequencyLevel} × 중대성{" "}
                    {currentDetail.importancyLevel}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Typography
                    variant="h2"
                    sx={{ fontWeight: 900, color: riskMeta.hex, lineHeight: 1 }}
                  >
                    {score}
                  </Typography>
                  <Stack spacing={0.6}>
                    <Chip color={riskMeta.color} label={riskMeta.level} />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 900, color: riskMeta.hex }}
                    >
                      {riskMeta.message}
                    </Typography>
                    {currentDetail.standard ? (
                      <Typography variant="caption" color="text.secondary">
                        기준 ID {currentDetail.standard.standard_id}
                      </Typography>
                    ) : null}
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

function FilePickerButton({
  label,
  required,
  file,
  fileUrl,
  onFileChange,
  disabled = false,
}) {
  return (
    <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 260 } }}>
      <ImageUploadBox
        label={label}
        required={required}
        file={file}
        fileUrl={resolveUploadFileUrl(fileUrl)}
        onFileChange={onFileChange}
        accept="image/png,image/jpeg,image/webp"
        compact
        disabled={disabled}
      />
    </Box>
  );
}
function MeasureStep({
  selectedHazards,
  evaluations,
  riskCriteria,
  measures,
  employees,
  missingItems = [],
  onMeasureChange,
}) {
  return (
    <Box>
      <SectionTitle
        icon={<WarningAmberIcon />}
        title="위험성 감소대책 진행 관리"
        description="개선 전 사진은 선택 입력이며, 개선 후 사진과 완료일을 넣으면 완료됩니다."
      />

      {missingItems.length ? (
        <Alert
          severity="warning"
          sx={{
            mb: 1.5,
            borderRadius: 2,
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 900, mb: 0.5 }}>
            다음 단계로 넘어가려면 아래 항목을 입력해주세요.
          </Typography>
          <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
            {missingItems.slice(0, 4).map((item) => (
              <Typography key={item} component="li" variant="body2">
                {item}
              </Typography>
            ))}
            {missingItems.length > 4 ? (
              <Typography component="li" variant="body2">
                외 {missingItems.length - 4}건
              </Typography>
            ) : null}
          </Stack>
        </Alert>
      ) : null}

      <Stack spacing={1.5}>
        {selectedHazards.map((hazard, index) => {
          const detail = getEvaluationDetail(
            riskCriteria,
            evaluations[hazard.id]
          );
          const measure = measures[hazard.id] || makeMeasureDraft(hazard);
          const statusMeta = getMeasureStatusMeta(hazard, measure);
          const completionLocked = isMeasureLocked(hazard, measure);
          const measurePlanLocked = isMeasureStepLocked(hazard, measure);
          const planFieldsLocked = completionLocked || measurePlanLocked;
          const hasAfterPhoto = hasMeasureFileOrUrl(
            measure.afterFile,
            measure.afterUrl
          );
          const handleAfterFileChange = (file) => {
            if (completionLocked) return;
            onMeasureChange(hazard.id, "afterFile", file);
            if (file && !measure.compDate)
              onMeasureChange(hazard.id, "compDate", getToday());
            if (file && !measure.afterRisk && detail.standard?.standard_id) {
              onMeasureChange(
                hazard.id,
                "afterRisk",
                String(detail.standard.standard_id)
              );
            }
          };

          return (
            <Box key={hazard.id} sx={{ ...panelSx, p: 2 }}>
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  justifyContent="space-between"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 0.75 }}
                    >
                      <Chip
                        color="primary"
                        label={`${index + 1}/${selectedHazards.length}`}
                      />
                      <Chip
                        color={detail.riskMeta.color}
                        label={`${detail.riskMeta.level} · ${detail.score}`}
                      />
                      {completionLocked || measurePlanLocked ? (
                        <Chip
                          color="success"
                          variant="outlined"
                          label={
                            completionLocked ? "개선 완료" : "대책 저장완료"
                          }
                        />
                      ) : null}
                    </Stack>
                    <Typography sx={{ fontWeight: 900 }}>
                      {hazard.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {hazard.equipment} · {hazard.process}
                    </Typography>
                  </Box>
                  <Chip
                    color={statusMeta.color}
                    label={statusMeta.label}
                    sx={{
                      fontWeight: 900,
                      alignSelf: { xs: "flex-start", sm: "center" },
                    }}
                  />
                </Stack>
                {measurePlanLocked ? (
                  <Alert severity="success" sx={{ borderRadius: "8px" }}>
                    이미 개선대책이 저장된 항목입니다. 저장된 대책은 읽기
                    전용이며, 개선 완료가 필요한 경우 개선 후 사진과
                    완료일만 이어서 등록합니다.
                  </Alert>
                ) : null}
                <TextField
                  label="개선 대책 내용"
                  value={measure.action}
                  onChange={(event) =>
                    onMeasureChange(hazard.id, "action", event.target.value)
                  }
                  fullWidth
                  multiline
                  minRows={3}
                  disabled={planFieldsLocked}
                  required={Boolean(detail.riskMeta.requiresImprove)}
                  placeholder="예: 회전부 방호덮개 설치, 작업 전 점검표 추가, 경고 표지 부착"
                />
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="담당자"
                      value={measure.emp_id || ""}
                      onChange={(event) =>
                        onMeasureChange(hazard.id, "emp_id", event.target.value)
                      }
                      fullWidth
                      disabled={planFieldsLocked || !employees.length}
                      required={Boolean(detail.riskMeta.requiresImprove)}
                    >
                      {employees.map((employee) => (
                        <MenuItem
                          key={employee.emp_id}
                          value={String(employee.emp_id)}
                        >
                          {employee.emp_name || employee.emp_id}
                          {employee.department_name
                            ? ` / ${employee.department_name}`
                            : ""}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="완료 예정일"
                      type="date"
                      value={measure.dueDate}
                      onChange={(event) =>
                        onMeasureChange(
                          hazard.id,
                          "dueDate",
                          event.target.value
                        )
                      }
                      fullWidth
                      required={Boolean(detail.riskMeta.requiresImprove)}
                      disabled={planFieldsLocked}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="개선 후 위험성"
                      value={measure.afterRisk || ""}
                      onChange={(event) =>
                        onMeasureChange(
                          hazard.id,
                          "afterRisk",
                          event.target.value
                        )
                      }
                      fullWidth
                      disabled={
                        completionLocked ||
                        (!hasAfterPhoto &&
                          Number(measure.improve_status || 0) < 20)
                      }
                    >
                      {riskCriteria.standards.map((standard) => (
                        <MenuItem
                          key={standard.standard_id}
                          value={String(standard.standard_id)}
                        >
                          {standard.manage_criteria ||
                            `기준 ${standard.standard_id}`}
                          {standard.risk_level_min && standard.risk_level_max
                            ? ` (${standard.risk_level_min}~${standard.risk_level_max})`
                            : ""}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="개선 완료일"
                      type="date"
                      value={measure.compDate || ""}
                      onChange={(event) =>
                        onMeasureChange(
                          hazard.id,
                          "compDate",
                          event.target.value
                        )
                      }
                      fullWidth
                      disabled={
                        completionLocked ||
                        (!hasAfterPhoto &&
                          Number(measure.improve_status || 0) < 20)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <FilePickerButton
                    label="개선 전 사진"
                    file={measure.beforeFile}
                    fileUrl={measure.beforeUrl}
                    disabled={planFieldsLocked}
                    onFileChange={(file) =>
                      onMeasureChange(hazard.id, "beforeFile", file)
                    }
                  />
                  <FilePickerButton
                    label="개선 후 사진"
                    file={measure.afterFile}
                    fileUrl={measure.afterUrl}
                    disabled={completionLocked}
                    onFileChange={handleAfterFileChange}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  개선 전 사진은 현장 상황에 따라 생략할 수 있습니다. 개선 후
                  사진을 등록하면 완료일과 개선 후 위험성을 함께 저장합니다.
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function ReviewStep({
  selectedHazards,
  evaluations,
  riskCriteria,
  measures,
  missingItems,
  submitted,
}) {
  const highRiskCount = selectedHazards.filter((hazard) => {
    const detail = getEvaluationDetail(riskCriteria, evaluations[hazard.id]);
    return detail.score >= 13;
  }).length;
  const needsMeasureCount = selectedHazards.filter((hazard) => {
    const detail = getEvaluationDetail(riskCriteria, evaluations[hazard.id]);
    return detail.riskMeta.requiresImprove || detail.score >= 8;
  }).length;

  return (
    <Box>
      <SectionTitle
        icon={<AssignmentTurnedInIcon />}
        title="평가 내용을 확인해주세요"
        description="빠진 내용이 없는지 확인한 뒤 승인합니다."
      />

      <Stack spacing={1.5}>
        <Box sx={{ ...panelSx, p: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                위험성평가 내용 확인
              </Typography>
              <Typography variant="body2" color="text.secondary">
                평가일자: {getToday()} | 선택 항목: {selectedHazards.length}건
              </Typography>
            </Box>
            <Chip
              color={submitted ? "success" : "warning"}
              label={submitted ? "승인 완료" : "작성 중"}
            />
          </Stack>
        </Box>

        <Grid container spacing={1.25}>
          {[
            {
              label: "총 식별 위험요인",
              value: selectedHazards.length,
              color: "#2563eb",
            },
            {
              label: "평가 완료",
              value: selectedHazards.length,
              color: "#16a34a",
            },
            { label: "대책 필요", value: needsMeasureCount, color: "#f59e0b" },
            { label: "높은 위험", value: highRiskCount, color: "#dc2626" },
          ].map((item) => (
            <Grid key={item.label} item xs={6} sm={3}>
              <Box sx={{ ...panelSx, p: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, color: item.color }}
                >
                  {item.value}건
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: missingItems.length ? "#f59e0b" : "#16a34a",
            borderRadius: "10px",
            bgcolor: missingItems.length ? "#fffbeb" : "#f0fdf4",
          }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              {missingItems.length ? (
                <WarningAmberIcon color="warning" />
              ) : (
                <CheckCircleIcon color="success" />
              )}
              <Typography sx={{ fontWeight: 900 }}>
                누락 항목 확인 ({missingItems.length}건)
              </Typography>
            </Stack>
            {missingItems.length ? (
              <Stack component="ul" sx={{ m: 0, pl: 3 }}>
                {missingItems.map((item) => (
                  <Typography key={item} component="li" variant="body2">
                    {item}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2">
                빠진 내용이 없습니다. 승인 버튼을 누르면 내용이 반영됩니다.
              </Typography>
            )}
          </Stack>
        </Box>

        <Stack spacing={1}>
          {selectedHazards.map((hazard) => {
            const detail = getEvaluationDetail(
              riskCriteria,
              evaluations[hazard.id]
            );
            const measure = measures[hazard.id] || {};
            const statusMeta = getMeasureStatusMeta(hazard, measure);
            return (
              <Box key={hazard.id} sx={{ ...panelSx, p: 1.35 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  justifyContent="space-between"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                      {hazard.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {hazard.equipment} · {hazard.process}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                      noWrap
                    >
                      대책: {measure.action || "미입력"}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 0.75 }}
                    >
                      <Chip
                        size="small"
                        color={
                          hasMeasureFileOrUrl(
                            measure.beforeFile,
                            measure.beforeUrl
                          )
                            ? "success"
                            : "default"
                        }
                        label="개선 전"
                      />
                      <Chip
                        size="small"
                        color={
                          hasMeasureFileOrUrl(
                            measure.afterFile,
                            measure.afterUrl
                          )
                            ? "success"
                            : "default"
                        }
                        label="개선 후"
                      />
                      {measure.dueDate ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`예정 ${measure.dueDate}`}
                        />
                      ) : null}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Chip
                      size="small"
                      color={statusMeta.color}
                      label={statusMeta.label}
                    />
                    <Chip
                      size="small"
                      color={detail.riskMeta.color}
                      label={`${detail.riskMeta.level} ${detail.score}`}
                    />
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Stack>
    </Box>
  );
}

function CompletionPanel({
  summary,
  pendingLoading,
  pendingError,
  onContinue,
  onViewProgress,
}) {
  const pendingCount = summary?.pendingCount;
  const canContinue =
    !pendingLoading &&
    (pendingError || summary?.reloadFailed || Number(pendingCount || 0) > 0);

  return (
    <Box>
      <Stack spacing={2.25}>
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            border: "1px solid",
            borderColor: "#bbf7d0",
            borderRadius: "12px",
            bgcolor: "#f0fdf4",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{
                  width: 52,
                  height: 52,
                  bgcolor: "#16a34a",
                  color: "#fff",
                }}
              >
                <CheckCircleIcon />
              </Avatar>
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 0.75 }}
                >
                  <Chip size="small" color="success" label="승인 완료" />
                  <Chip
                    size="small"
                    variant="outlined"
                    label="자동 저장 정리 완료"
                  />
                </Stack>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, letterSpacing: 0 }}
                >
                  위험성평가가 승인되었습니다
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  개선 중인 항목은 다시 열어 개선 후 사진과 완료일을 이어서
                  등록할 수 있습니다.
                </Typography>
              </Box>
            </Stack>
            <Chip
              label={
                summary?.submittedAt
                  ? `완료 ${formatDraftTime(summary.submittedAt)}`
                  : "완료"
              }
              sx={{
                height: 40,
                px: 1,
                fontWeight: 900,
                bgcolor: "#fff",
                border: "1px solid #bbf7d0",
              }}
            />
          </Stack>
        </Box>

        <Grid container spacing={1.25}>
          {[
            {
              label: "처리 항목",
              value: `${summary?.submittedCount || 0}건`,
              color: "#2563eb",
            },
            {
              label: "승인 완료",
              value: `${summary?.savedCount || 0}건`,
              color: "#16a34a",
            },
            {
              label: "개선대책",
              value: `${summary?.improvementCount || 0}건`,
              color: "#f59e0b",
            },
            {
              label: "남은 미평가",
              value:
                pendingCount === null || pendingCount === undefined
                  ? "-"
                  : `${pendingCount}건`,
              color: "#0f172a",
            },
          ].map((item) => (
            <Grid key={item.label} item xs={6} md={3}>
              <Box sx={{ ...panelSx, p: 1.6, minHeight: 92 }}>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ mt: 0.8, fontWeight: 900, color: item.color }}
                >
                  {item.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {pendingLoading ? (
          <Alert severity="info" sx={{ borderRadius: "8px" }}>
            남은 미평가 항목을 다시 불러오는 중입니다.
          </Alert>
        ) : null}
        {pendingError || summary?.reloadFailed ? (
          <Alert severity="warning" sx={{ borderRadius: "8px" }}>
            승인은 완료됐지만 남은 미평가 항목을 다시 불러오지 못했습니다. 계속
            평가 버튼을 누르면 목록을 다시 요청합니다.
          </Alert>
        ) : null}
        {!pendingLoading && !pendingError && pendingCount === 0 ? (
          <Alert severity="success" sx={{ borderRadius: "8px" }}>
            이번 달 미평가 항목이 모두 처리되었습니다. 개선 중인 항목은 첫
            화면에서 계속 확인할 수 있습니다.
          </Alert>
        ) : null}

        <Box sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<AssignmentTurnedInIcon />}
              onClick={onViewProgress}
            >
              진행 현황 다시 보기
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              disabled={!canContinue}
              onClick={onContinue}
            >
              다음 미평가 항목 계속 평가
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

export function RiskAssessmentWizard({ initialStep = 0 } = {}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { group_code, project_code } = useParams();
  const location = useLocation();
  const initialStepIndex = normalizeWizardStepIndex(initialStep);
  const focusRequest = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const registerCauseId = Number(params.get("register_cause_id") || 0);
    const locationId = Number(params.get("location_id") || 0);
    const locationIds = parseWizardLocationIds(params.get("location_ids"));
    if (locationId > 0 && !locationIds.includes(locationId)) {
      locationIds.unshift(locationId);
    }
    const dangerCauseId =
      Number(params.get("danger_cause_id") || 0) ||
      Number(location.state?.selectedSituation?.danger_cause_id || 0);
    const locationKey = locationIds.join(",");
    return {
      hasFocus:
        registerCauseId > 0 || dangerCauseId > 0 || locationIds.length > 0,
      registerCauseId: registerCauseId > 0 ? registerCauseId : null,
      dangerCauseId: dangerCauseId > 0 ? dangerCauseId : null,
      locationId: locationId > 0 ? locationId : null,
      locationIds,
      locationKey,
      locationName: params.get("location_name") || "",
      assessmentScope: params.get("assessment_scope") || "",
    };
  }, [location.search, location.state]);
  const storageKey = useMemo(
    () => getDraftStorageKey(group_code, project_code),
    [group_code, project_code]
  );
  const [draftId, setDraftId] = useState(createDraftId);
  const [drafts, setDrafts] = useState([]);
  const [draftPickerOpen, setDraftPickerOpen] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [draftAutoSaveEnabled, setDraftAutoSaveEnabled] = useState(false);
  const [activeStep, setActiveStep] = useState(initialStepIndex);
  const [groups, setGroups] = useState([]);
  const [improvementGroups, setImprovementGroups] = useState([]);
  const [selectedHazardIds, setSelectedHazardIds] = useState(() => new Set());
  const [employees, setEmployees] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState(false);
  const [pendingLoaded, setPendingLoaded] = useState(false);
  const [workItems, setWorkItems] = useState({
    improvementItems: [],
    pendingCount: 0,
  });
  const [workItemsLoading, setWorkItemsLoading] = useState(false);
  const [workItemsError, setWorkItemsError] = useState(false);
  const [riskCriteria, setRiskCriteria] = useState(EMPTY_RISK_CRITERIA);
  const [criteriaLoading, setCriteriaLoading] = useState(false);
  const [criteriaError, setCriteriaError] = useState(false);
  const [evaluations, setEvaluations] = useState(makeInitialEvaluations);
  const [measures, setMeasures] = useState({});
  const [currentHazardIndex, setCurrentHazardIndex] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completionSummary, setCompletionSummary] = useState(null);

  const allGroups = useMemo(
    () => [...improvementGroups, ...groups],
    [groups, improvementGroups]
  );
  const allLineGroups = useMemo(
    () => createLineEvaluationGroups(allGroups),
    [allGroups]
  );
  const allSelectableGroups = useMemo(
    () => [...allGroups, ...allLineGroups],
    [allGroups, allLineGroups]
  );
  const allHazards = useMemo(
    () => flattenHazards(allSelectableGroups),
    [allSelectableGroups]
  );
  const selectedHazards = useMemo(
    () => allHazards.filter((hazard) => selectedHazardIds.has(hazard.id)),
    [allHazards, selectedHazardIds]
  );
  const hasRiskCriteria =
    riskCriteria.frequencies.length > 0 &&
    riskCriteria.importancies.length > 0 &&
    riskCriteria.standards.length > 0;

  const resetWizardState = useCallback((nextDraftId = createDraftId()) => {
    setDraftId(nextDraftId);
    setActiveStep(initialStepIndex);
    setGroups([]);
    setImprovementGroups([]);
    setSelectedHazardIds(new Set());
    setEvaluations(makeInitialEvaluations());
    setMeasures({});
    setCurrentHazardIndex(0);
    setSubmitted(false);
    setSaving(false);
    setCompletionSummary(null);
    setDraftAutoSaveEnabled(true);
    setPendingError(false);
    setPendingLoaded(false);
  }, [initialStepIndex]);

  const startNewDraft = useCallback(() => {
    resetWizardState(createDraftId());
    setDraftReady(true);
    setDraftPickerOpen(false);
  }, [resetWizardState]);

  const selectDraft = useCallback((draft) => {
    setDraftId(draft.id || createDraftId());
    setActiveStep(normalizeDraftActiveStep(draft));
    setGroups(
      Array.isArray(draft.groups) && draft.groups.length ? draft.groups : []
    );
    setImprovementGroups(
      Array.isArray(draft.improvementGroups) ? draft.improvementGroups : []
    );
    setSelectedHazardIds(
      new Set(
        Array.isArray(draft.selectedHazardIds) && draft.selectedHazardIds.length
          ? draft.selectedHazardIds
          : []
      )
    );
    setEvaluations(
      draft.evaluations && Object.keys(draft.evaluations).length
        ? draft.evaluations
        : makeInitialEvaluations()
    );
    setMeasures(normalizeDraftMeasures(draft.measures || {}));
    setCurrentHazardIndex(Math.max(Number(draft.currentHazardIndex || 0), 0));
    setSubmitted(Boolean(draft.submitted));
    setCompletionSummary(null);
    setDraftAutoSaveEnabled(true);
    setPendingError(false);
    setPendingLoaded(false);
    setDraftReady(true);
    setDraftPickerOpen(false);
  }, []);

  const selectWorkItem = useCallback(
    (item) => {
      const workflowType = "improvement";
      const workRows =
        Array.isArray(item.items) && item.items.length ? item.items : [item];
      const nextGroups = normalizeImprovementHazardGroups(
        workRows,
        workflowType
      );
      const selectableGroups = item.isLineWorkItemGroup
        ? createLineEvaluationGroups(nextGroups)
        : nextGroups;
      const nextHazards = flattenHazards(selectableGroups);
      const nextEvaluations = {};
      const nextMeasures = {};

      nextHazards.forEach((hazard) => {
        const evaluationSeed = {
          frequency_id: hazard.frequency_id,
          importancy_id: hazard.importancy_id,
        };
        nextEvaluations[hazard.id] = hasRiskCriteria
          ? normalizeEvaluationForCriteria(evaluationSeed, riskCriteria)
          : evaluationSeed;
        nextMeasures[hazard.id] = makeMeasureDraft(hazard);
      });

      setDraftId(createDraftId());
      setGroups([]);
      setImprovementGroups(nextGroups);
      setSelectedHazardIds(new Set(nextHazards.map((hazard) => hazard.id)));
      setEvaluations(nextEvaluations);
      setMeasures(nextMeasures);
      setCurrentHazardIndex(0);
      setSubmitted(false);
      setCompletionSummary(null);
      setDraftAutoSaveEnabled(false);
      setPendingError(false);
      setPendingLoaded(true);
      setDraftReady(true);
      setDraftPickerOpen(false);
      setActiveStep(2);
    },
    [hasRiskCriteria, riskCriteria]
  );

  const deleteDraft = useCallback(
    (targetId) => {
      const nextDrafts = drafts.filter((draft) => draft.id !== targetId);
      writeWizardDrafts(storageKey, nextDrafts);
      setDrafts(nextDrafts);
      if (!nextDrafts.length) startNewDraft();
    },
    [drafts, startNewDraft, storageKey]
  );

  const clearCurrentDraft = useCallback(() => {
    const nextDrafts = readWizardDrafts(storageKey).filter(
      (draft) => draft.id !== draftId
    );
    writeWizardDrafts(storageKey, nextDrafts);
    setDrafts(nextDrafts);
  }, [draftId, storageKey]);

  useEffect(() => {
    const rawDrafts = readWizardDrafts(storageKey);
    const storedDrafts = rawDrafts
      .filter((draft) => !isExistingWorkItemDraft(draft))
      .map((draft) => ({
        ...draft,
        schemaVersion: WIZARD_DRAFT_SCHEMA_VERSION,
        activeStep: normalizeDraftActiveStep(draft),
      }));
    const shouldRewriteDrafts =
      storedDrafts.length !== rawDrafts.length ||
      rawDrafts.some(
        (draft) =>
          !isExistingWorkItemDraft(draft) &&
          (Number(draft.schemaVersion) !== WIZARD_DRAFT_SCHEMA_VERSION ||
            Number(draft.activeStep) !== normalizeDraftActiveStep(draft))
      );
    if (shouldRewriteDrafts) {
      writeWizardDrafts(storageKey, storedDrafts);
    }
    setDrafts(storedDrafts);
    resetWizardState(createDraftId());
    if (focusRequest.hasFocus) {
      setDraftReady(true);
      setDraftPickerOpen(false);
      return;
    }
    setDraftReady(false);
    setDraftPickerOpen(true);
  }, [focusRequest.hasFocus, resetWizardState, storageKey]);

  const loadRiskCriteria = useCallback(() => {
    if (!group_code || !project_code) return undefined;

    let ignore = false;
    setCriteriaLoading(true);
    setCriteriaError(false);
    setRiskCriteria(EMPTY_RISK_CRITERIA);

    axiosInstance
      .get(`/risk/${group_code}/${project_code}/wizard/risk-criteria`)
      .then((response) => {
        if (ignore) return;
        setRiskCriteria(normalizeRiskCriteria(response.data));
      })
      .catch(() => {
        if (ignore) return;
        setCriteriaError(true);
        setRiskCriteria(EMPTY_RISK_CRITERIA);
      })
      .finally(() => {
        if (!ignore) setCriteriaLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [group_code, project_code]);

  const loadWizardWorkItems = useCallback(() => {
    if (!group_code || !project_code) return undefined;

    let ignore = false;
    setWorkItemsLoading(true);
    setWorkItemsError(false);

    Promise.all([
      axiosInstance.get(`/risk/${group_code}/${project_code}/wizard/work-items`, {
        params: { eval_date: getToday() },
      }),
      axiosInstance.get(`/risk/${group_code}/${project_code}/wizard/pending-hazards`, {
        params: { eval_date: getToday() },
      }),
    ])
      .then(([response, pendingResponse]) => {
        if (ignore) return;
        const rows = Array.isArray(response.data?.rows)
          ? response.data.rows
          : [];
        const pendingRows = Array.isArray(pendingResponse.data?.rows)
          ? pendingResponse.data.rows
          : [];
        setWorkItems({
          improvementItems: groupImprovementWorkItems(rows),
          pendingCount: pendingRows.length,
        });
        if (
          Array.isArray(response.data?.employees) &&
          response.data.employees.length
        ) {
          setEmployees(response.data.employees);
        }
      })
      .catch(() => {
        if (ignore) return;
        setWorkItemsError(true);
        setWorkItems({ improvementItems: [], pendingCount: 0 });
      })
      .finally(() => {
        if (!ignore) setWorkItemsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [group_code, project_code]);

  useEffect(() => {
    if (!draftPickerOpen) return undefined;
    return loadWizardWorkItems();
  }, [draftPickerOpen, loadWizardWorkItems]);

  const loadPendingHazards = useCallback(
    ({ applyGroups = true } = {}) => {
      if (!group_code || !project_code) return undefined;

      let ignore = false;
      setPendingLoading(true);
      setPendingError(false);

      Promise.all([
        axiosInstance.get(
          `/risk/${group_code}/${project_code}/wizard/pending-hazards`,
          {
            params: { eval_date: getToday() },
          }
        ),
        axiosInstance.get(
          `/risk/${group_code}/${project_code}/wizard/improvements`,
          {
            params: { eval_date: getToday() },
          }
        ),
        axiosInstance
          .get(`/risk/${group_code}/${project_code}/wizard/step-items`, {
            params: { eval_date: getToday() },
          })
          .catch((error) => {
            if (error?.response?.status === 404) {
              return axiosInstance.get(
                `/risk/${group_code}/${project_code}/wizard/work-items`,
                { params: { eval_date: getToday() } }
              );
            }
            throw error;
          })
          .catch(() => ({ data: { rows: [], employees: [] } })),
        axiosInstance
          .get(`/risk/${group_code}/${project_code}/worker-hazard-proposals`, {
            params: { status: "approved" },
          })
          .catch(() => ({ data: [] })),
      ])
        .then(([response, improvementResponse, stepItemsResponse, workerProposalResponse]) => {
          if (ignore) return;
          const data = response.data || {};
          const improvementData = improvementResponse.data || {};
          const stepItemsData = stepItemsResponse.data || {};
          const workerProposalSourceMap = buildWorkerProposalSourceMap(
            workerProposalResponse.data || []
          );
          const nextGroups = normalizePendingHazardGroups(
            data.rows || [],
            workerProposalSourceMap
          );
          const nextImprovementGroups = normalizeImprovementHazardGroups(
            (stepItemsData.rows || []).filter(isWizardContinuationRow),
            "progress"
          );
          setEmployees(
            Array.isArray(data.employees) && data.employees.length
              ? data.employees
              : Array.isArray(stepItemsData.employees) &&
                stepItemsData.employees.length
              ? stepItemsData.employees
              : Array.isArray(improvementData.employees)
              ? improvementData.employees
              : []
          );

          if (applyGroups) {
            const focusHazardIds = focusRequest.hasFocus
              ? flattenHazards(
                  focusRequest.assessmentScope === "line"
                    ? createLineEvaluationGroups(nextGroups)
                    : nextGroups
                )
                  .filter((hazard) => {
                    if (
                      focusRequest.registerCauseId &&
                      Number(hazard.register_cause_id) === focusRequest.registerCauseId
                    ) {
                      return true;
                    }
                    const hazardLocationIds = Array.isArray(hazard.location_ids)
                      ? hazard.location_ids
                      : [];
                    const matchesLocation =
                      focusRequest.locationIds.length > 0 &&
                      hazardLocationIds.some((locationId) =>
                        focusRequest.locationIds.includes(locationId)
                      );
                    return (
                      (focusRequest.dangerCauseId &&
                        Number(hazard.danger_cause_id) === focusRequest.dangerCauseId) ||
                      matchesLocation
                    );
                  })
                  .map((hazard) => hazard.id)
              : [];
            setGroups(nextGroups);
            setImprovementGroups(nextImprovementGroups);
            setSelectedHazardIds(new Set(focusHazardIds));
            setEvaluations({});
            setMeasures({});
            setCurrentHazardIndex(0);
            setSubmitted(false);
            if (focusRequest.hasFocus && focusHazardIds.length > 0) {
              setActiveStep(0);
            }
            if (focusRequest.hasFocus && focusHazardIds.length > 0) {
              toast.success(
                focusRequest.locationIds.length > 0
                  ? "라인에 연결된 평가대상을 모두 선택했습니다."
                  : "연결된 평가대상을 선택했습니다. 다음 단계로 진행해주세요."
              );
            } else if (focusRequest.hasFocus) {
              toast.info("이미 이번 달 평가가 완료됐거나 평가대상 목록에 없는 항목입니다.");
            }
          }
          setPendingLoaded(true);
        })
        .catch(() => {
          if (ignore) return;
          setPendingError(true);
          setPendingLoaded(true);
        })
        .finally(() => {
          if (!ignore) setPendingLoading(false);
        });

      return () => {
        ignore = true;
      };
    },
    [
      focusRequest.dangerCauseId,
      focusRequest.assessmentScope,
      focusRequest.hasFocus,
      focusRequest.locationIds,
      focusRequest.registerCauseId,
      group_code,
      project_code,
    ]
  );

  const reloadPendingHazards = useCallback(async () => {
    if (!group_code || !project_code) return [];

    setPendingLoading(true);
    setPendingError(false);

    try {
      const [response, improvementResponse, stepItemsResponse, workerProposalResponse] =
        await Promise.all([
          axiosInstance.get(
            `/risk/${group_code}/${project_code}/wizard/pending-hazards`,
            {
              params: { eval_date: getToday() },
            }
          ),
          axiosInstance.get(
            `/risk/${group_code}/${project_code}/wizard/improvements`,
            {
              params: { eval_date: getToday() },
            }
          ),
          axiosInstance
            .get(`/risk/${group_code}/${project_code}/wizard/step-items`, {
              params: { eval_date: getToday() },
            })
            .catch((error) => {
              if (error?.response?.status === 404) {
                return axiosInstance.get(
                  `/risk/${group_code}/${project_code}/wizard/work-items`,
                  { params: { eval_date: getToday() } }
                );
              }
              throw error;
            })
            .catch(() => ({ data: { rows: [], employees: [] } })),
          axiosInstance
            .get(
              `/risk/${group_code}/${project_code}/worker-hazard-proposals`,
              {
                params: { status: "approved" },
              }
            )
            .catch(() => ({ data: [] })),
        ]);
      const data = response.data || {};
      const improvementData = improvementResponse.data || {};
      const stepItemsData = stepItemsResponse.data || {};
      const workerProposalSourceMap = buildWorkerProposalSourceMap(
        workerProposalResponse.data || []
      );
      const nextGroups = normalizePendingHazardGroups(
        data.rows || [],
        workerProposalSourceMap
      );
      const nextImprovementGroups = normalizeImprovementHazardGroups(
        (stepItemsData.rows || []).filter(isWizardContinuationRow),
        "progress"
      );

      setEmployees(
        Array.isArray(data.employees) && data.employees.length
          ? data.employees
          : Array.isArray(stepItemsData.employees) &&
            stepItemsData.employees.length
          ? stepItemsData.employees
          : Array.isArray(improvementData.employees)
          ? improvementData.employees
          : []
      );
      setGroups(nextGroups);
      setImprovementGroups(nextImprovementGroups);
      setSelectedHazardIds(new Set());
      setEvaluations({});
      setMeasures({});
      setCurrentHazardIndex(0);
      setPendingLoaded(true);

      return nextGroups;
    } catch (error) {
      setPendingError(true);
      setPendingLoaded(true);
      throw error;
    } finally {
      setPendingLoading(false);
    }
  }, [group_code, project_code]);

  const refreshWizardStepSnapshot = useCallback(async () => {
    if (!group_code || !project_code) return;

    const response = await axiosInstance
      .get(`/risk/${group_code}/${project_code}/wizard/step-items`, {
        params: { eval_date: getToday() },
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          return axiosInstance.get(
            `/risk/${group_code}/${project_code}/wizard/work-items`,
            { params: { eval_date: getToday() } }
          );
        }
        throw error;
      });

    const rows = Array.isArray(response.data?.rows) ? response.data.rows : [];
    const patchMap = buildStepItemPatchMap(rows);
    if (!patchMap.size) return;

    setGroups((prev) => mergeStepItemPatchesIntoGroups(prev, patchMap));
    setImprovementGroups((prev) =>
      mergeStepItemPatchesIntoGroups(prev, patchMap)
    );

    setEvaluations((prev) => {
      const next = { ...prev };
      allHazards.forEach((hazard) => {
        const patch = patchMap.get(Number(hazard.register_cause_id));
        if (!patch?.frequency_id || !patch?.importancy_id) return;
        next[hazard.id] = normalizeEvaluationForCriteria(
          {
            frequency_id: patch.frequency_id,
            importancy_id: patch.importancy_id,
          },
          riskCriteria
        );
      });
      return next;
    });

    setMeasures((prev) => {
      const next = { ...prev };
      allHazards.forEach((hazard) => {
        const patch = patchMap.get(Number(hazard.register_cause_id));
        if (!patch || !(patch.improve_id || patch.wizard_step >= 3)) return;
        next[hazard.id] = {
          ...(next[hazard.id] || makeMeasureDraft(hazard)),
          action: patch.improve_plan || next[hazard.id]?.action || "",
          emp_id: patch.emp_id ? String(patch.emp_id) : next[hazard.id]?.emp_id || "",
          dueDate: patch.expected_date || next[hazard.id]?.dueDate || "",
          beforeUrl: patch.improve_before || next[hazard.id]?.beforeUrl || "",
          afterUrl: patch.improve_after || next[hazard.id]?.afterUrl || "",
          afterRisk:
            patch.improve_after_risk || next[hazard.id]?.afterRisk || "",
          compDate: patch.comp_date || next[hazard.id]?.compDate || "",
          improve_status:
            patch.improve_status || next[hazard.id]?.improve_status || 0,
        };
      });
      return next;
    });
  }, [allHazards, group_code, project_code, riskCriteria]);

  useEffect(() => loadRiskCriteria(), [loadRiskCriteria]);

  useEffect(() => {
    if (!draftReady || draftPickerOpen || completionSummary) return undefined;
    if (typeof window === "undefined" || typeof document === "undefined")
      return undefined;

    const syncLatestStepData = () => {
      refreshWizardStepSnapshot().catch(() => {
        // Focus refresh is best-effort; normal save/reload flows still handle errors.
      });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") syncLatestStepData();
    };

    window.addEventListener("focus", syncLatestStepData);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", syncLatestStepData);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    completionSummary,
    draftPickerOpen,
    draftReady,
    refreshWizardStepSnapshot,
  ]);

  useEffect(() => {
    if (!draftReady || draftPickerOpen || pendingLoaded) return undefined;
    return loadPendingHazards({
      applyGroups: groups.length + improvementGroups.length === 0,
    });
  }, [
    draftPickerOpen,
    draftReady,
    groups.length,
    improvementGroups.length,
    loadPendingHazards,
    pendingLoaded,
  ]);

  useEffect(() => {
    if (activeStep !== 1) return undefined;
    return loadRiskCriteria();
  }, [activeStep, loadRiskCriteria]);

  useEffect(() => {
    if (activeStep !== 1 || !selectedHazards.length) return;
    const firstEditableIndex = selectedHazards.findIndex(
      (hazard) => !isEvaluationLocked(hazard)
    );
    if (
      firstEditableIndex >= 0 &&
      isEvaluationLocked(selectedHazards[currentHazardIndex])
    ) {
      setCurrentHazardIndex(firstEditableIndex);
    }
  }, [activeStep, currentHazardIndex, selectedHazards]);

  useEffect(() => {
    if (
      !draftReady ||
      !draftAutoSaveEnabled ||
      draftPickerOpen ||
      completionSummary ||
      activeStep < 0
    )
      return undefined;

    const timer = setTimeout(() => {
      const now = new Date().toISOString();
      const previousDrafts = readWizardDrafts(storageKey);
      const existing = previousDrafts.find((draft) => draft.id === draftId);
      const draft = {
        id: draftId,
        schemaVersion: WIZARD_DRAFT_SCHEMA_VERSION,
        title: existing?.title || "위험성평가 Wizard",
        groupCode: group_code,
        projectCode: project_code,
        createdAt: existing?.createdAt || now,
        updatedAt: now,
        activeStep,
        currentHazardIndex,
        groups,
        improvementGroups,
        selectedHazardIds: Array.from(selectedHazardIds),
        evaluations,
        measures: normalizeDraftMeasures(measures),
        submitted,
      };
      const nextDrafts = [
        draft,
        ...previousDrafts.filter((item) => item.id !== draftId),
      ].slice(0, 20);
      writeWizardDrafts(storageKey, nextDrafts);
      setDrafts(nextDrafts);
    }, 500);

    return () => clearTimeout(timer);
  }, [
    activeStep,
    completionSummary,
    currentHazardIndex,
    draftId,
    draftPickerOpen,
    draftReady,
    draftAutoSaveEnabled,
    evaluations,
    group_code,
    groups,
    improvementGroups,
    measures,
    project_code,
    selectedHazardIds,
    storageKey,
    submitted,
  ]);

  useEffect(() => {
    setEvaluations((prev) =>
      Object.entries(prev).reduce((acc, [hazardId, evaluation]) => {
        acc[hazardId] = normalizeEvaluationForCriteria(
          evaluation,
          riskCriteria
        );
        return acc;
      }, {})
    );
  }, [riskCriteria]);

  useEffect(() => {
    setEvaluations((prev) => {
      const next = { ...prev };
      selectedHazards.forEach((hazard) => {
        if (!next[hazard.id]) {
          next[hazard.id] =
            hazard.frequency_id && hazard.importancy_id
              ? normalizeEvaluationForCriteria(
                  {
                    frequency_id: hazard.frequency_id,
                    importancy_id: hazard.importancy_id,
                  },
                  riskCriteria
                )
              : makeDefaultEvaluation(riskCriteria);
        }
      });
      return next;
    });
    setMeasures((prev) => {
      const next = { ...prev };
      selectedHazards.forEach((hazard) => {
        if (!next[hazard.id]) next[hazard.id] = makeMeasureDraft(hazard);
      });
      return next;
    });
    setCurrentHazardIndex((prev) =>
      Math.min(prev, Math.max(0, selectedHazards.length - 1))
    );
  }, [riskCriteria, selectedHazards]);

  // eslint-disable-next-line no-unused-vars
  const legacyMissingItems = useMemo(() => {
    const missing = [];
    if (!selectedHazards.length) missing.push("선택된 위험요인이 없습니다.");
    selectedHazards.forEach((hazard) => {
      const measure = measures[hazard.id] || {};
      if (!String(measure.action || "").trim())
        missing.push(`${hazard.title}: 개선 대책 내용`);
      if (!measure.assignee) missing.push(`${hazard.title}: 담당자`);
      if (!measure.dueDate) missing.push(`${hazard.title}: 완료 예정일`);
    });
    return missing;
  }, [measures, selectedHazards]);

  const evaluationMissingItems = useMemo(() => {
    const missing = [];
    if (!selectedHazards.length)
      missing.push("선택된 유해·위험요인이 없습니다.");

    selectedHazards.forEach((hazard) => {
      if (isEvaluationLocked(hazard)) return;
      const evaluation = normalizeEvaluationForCriteria(
        evaluations[hazard.id],
        riskCriteria
      );
      if (!evaluation.frequency_id || !evaluation.importancy_id) {
        missing.push(`${hazard.title}: 가능성/중대성`);
      }
    });

    return missing;
  }, [evaluations, riskCriteria, selectedHazards]);

  const missingItems = useMemo(() => {
    const missing = [];
    if (!selectedHazards.length)
      missing.push("선택된 유해·위험요인이 없습니다.");

    selectedHazards.forEach((hazard) => {
      if (!hazard.register_cause_id)
        missing.push(`${hazard.title}: register_cause_id`);

      const detail = getEvaluationDetail(riskCriteria, evaluations[hazard.id]);
      const measure = measures[hazard.id] || {};
      if (isMeasureLocked(hazard, measure) || isMeasureStepLocked(hazard, measure)) return;
      const action = String(measure.action || "").trim();
      const requiresImprove = Boolean(detail.riskMeta.requiresImprove);
      const hasBefore = hasMeasureFileOrUrl(
        measure.beforeFile,
        measure.beforeUrl
      );
      const hasAfter = hasMeasureFileOrUrl(measure.afterFile, measure.afterUrl);
      const wantsComplete = Boolean(
        hasAfter || Number(measure.improve_status || 0) >= 20
      );
      const hasMeasureInput = Boolean(
        action || measure.emp_id || hasBefore || hasAfter
      );

      if (requiresImprove || hasMeasureInput) {
        if (!action) missing.push(`${hazard.title}: 개선대책 내용`);
        if (action.length > 50)
          missing.push(`${hazard.title}: 개선대책 50자 이내`);
        if (!measure.emp_id) missing.push(`${hazard.title}: 담당자`);
        if (!measure.dueDate) missing.push(`${hazard.title}: 완료 예정일`);
        if (wantsComplete) {
          if (!hasAfter) missing.push(`${hazard.title}: 개선 후 사진`);
          if (!measure.afterRisk)
            missing.push(`${hazard.title}: 개선 후 위험성`);
          if (!measure.compDate) missing.push(`${hazard.title}: 개선 완료일`);
        }
      }
    });

    return missing;
  }, [evaluations, measures, riskCriteria, selectedHazards]);

  const progress = useMemo(() => {
    if (submitted) return 100;
    if (activeStep === 0) return selectedHazards.length ? 25 : 10;
    if (activeStep === 1)
      return evaluationMissingItems.length ? 45 : 55;
    if (activeStep === 2) return missingItems.length ? 72 : 84;
    return missingItems.length ? 90 : 96;
  }, [
    activeStep,
    evaluationMissingItems.length,
    missingItems.length,
    selectedHazards.length,
    submitted,
  ]);

  const toggleHazard = (hazardId) => {
    setSelectedHazardIds((prev) => {
      const next = new Set(prev);
      if (next.has(hazardId)) next.delete(hazardId);
      else next.add(hazardId);
      return next;
    });
    setSubmitted(false);
  };

  const toggleHazardSet = (ids = []) => {
    if (!ids.length) return;
    const allChecked = ids.every((id) => selectedHazardIds.has(id));
    setSelectedHazardIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (allChecked) next.delete(id);
        else next.add(id);
      });
      return next;
    });
    setSubmitted(false);
  };

  const addHazard = ({ groupId, title, source }) => {
    const id = `hz-custom-${Date.now()}`;
    setGroups((prev) =>
      prev.map((group) =>
        group.id === groupId
          ? { ...group, hazards: [...group.hazards, { id, title, source }] }
          : group
      )
    );
    setSelectedHazardIds((prev) => new Set([...prev, id]));
    toast.success("위험요인이 추가되고 선택되었습니다.");
  };

  const updateEvaluation = (hazardId, key, value) => {
    setEvaluations((prev) => ({
      ...prev,
      [hazardId]: {
        ...(prev[hazardId] || makeDefaultEvaluation(riskCriteria)),
        [key]: value,
      },
    }));
    setSubmitted(false);
  };

  const updateMeasure = (hazardId, key, value) => {
    setMeasures((prev) => ({
      ...prev,
      [hazardId]: {
        ...(prev[hazardId] ||
          makeMeasureDraft(
            allHazards.find((hazard) => hazard.id === hazardId)
          )),
        [key]: value,
      },
    }));
    setSubmitted(false);
  };

  const goNext = () => {
    if (activeStep === 0 && !selectedHazards.length) {
      toast.warning("위험요인을 하나 이상 선택해주세요.");
      return;
    }
    if (activeStep === 1 && !hasRiskCriteria) {
      toast.warning("가능성, 중대성, 허용 기준을 먼저 불러와야 합니다.");
      return;
    }
    if (activeStep === 1 && evaluationMissingItems.length) {
      toast.warning("가능성과 중대성을 먼저 입력해주세요.");
      return;
    }
    if (activeStep === 2 && missingItems.length) {
      toast.warning("감소대책의 누락 항목을 먼저 입력해주세요.");
      return;
    }
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const goPrev = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const continuePendingEvaluation = () => {
    setCompletionSummary(null);
    setSubmitted(false);
    setDraftAutoSaveEnabled(true);
    setDraftId(createDraftId());
    setActiveStep(0);
    setSelectedHazardIds(new Set());
    setEvaluations({});
    setMeasures({});
    setCurrentHazardIndex(0);

    if (pendingError || !countHazardsInGroups(groups)) {
      reloadPendingHazards().catch(() => {
        toast.error("남은 미평가 항목을 다시 불러오지 못했습니다.");
      });
    }
  };

  const viewWizardProgress = () => {
    setCompletionSummary(null);
    setSubmitted(false);
    setDraftAutoSaveEnabled(false);
    setDraftId(createDraftId());
    setActiveStep(0);
    setDraftReady(false);
    setDraftPickerOpen(true);
  };

  const uploadImprovementImage = async (file, kind) => {
    if (!isUploadableFile(file)) return null;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("kind", kind);

    const response = await axiosInstance.post(
      `/risk/${group_code}/${project_code}/wizard/file`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    return response.data?.url || null;
  };

  const submit = async () => {
    if (missingItems.length) {
      toast.warning("누락 항목을 먼저 해결해주세요.");
      return;
    }

    setSaving(true);
    try {
      const hazardsForSubmit = selectedHazards.filter(
        (hazard) => {
          const measure = measures[hazard.id] || {};
          return (
            Number(hazard.eval_status || 0) < 20 ||
            !isMeasureLocked(hazard, measure)
          );
        }
      );

      if (!hazardsForSubmit.length) {
        toast.info("저장할 개선 항목이 없습니다.");
        setSaving(false);
        return;
      }

      const evaluationsPayload = await Promise.all(
        hazardsForSubmit.map(async (hazard) => {
          const detail = getEvaluationDetail(
            riskCriteria,
            evaluations[hazard.id]
          );
          const measure = measures[hazard.id] || {};
          const action = String(measure.action || "").trim();
          const beforeUrl =
            (isUploadableFile(measure.beforeFile)
              ? await uploadImprovementImage(
                  measure.beforeFile,
                  "improve_before"
                )
              : null) ||
            measure.beforeUrl ||
            hazard.improve_before ||
            null;
          const afterUrl =
            (isUploadableFile(measure.afterFile)
              ? await uploadImprovementImage(measure.afterFile, "improve_after")
              : null) ||
            measure.afterUrl ||
            hazard.improve_after ||
            null;
          const shouldSaveImprovement = Boolean(
            detail.riskMeta.requiresImprove ||
              action ||
              measure.emp_id ||
              beforeUrl ||
              afterUrl
          );
          const isCompleted = Boolean(afterUrl);
          const focusSourceLocationId =
            focusRequest.assessmentScope === "line" && focusRequest.locationId
              ? Number(focusRequest.locationId)
              : null;
          const savedSourceLocationId = hazard.source_location_id
            ? Number(hazard.source_location_id)
            : null;
          const sourceLocationId = focusSourceLocationId || savedSourceLocationId;
          const evalScope =
            sourceLocationId &&
            (focusRequest.assessmentScope === "line" ||
              hazard.eval_scope === "line")
              ? "line"
              : "asset";
          const wizardGroupKey =
            hazard.wizard_group_key ||
            createWizardGroupKey({
              scope: evalScope,
              sourceLocationId,
              registerAssetId: hazard.register_asset_id,
              registerCauseId: hazard.register_cause_id,
              draftId,
            });

          return {
            eval_id: hazard.eval_id || undefined,
            register_cause_id: Number(hazard.register_cause_id),
            frequency_id: detail.frequency_id,
            importancy_id: detail.importancy_id,
            eval_status: 20,
            wizard_step: 4,
            wizard_group_key: wizardGroupKey,
            eval_scope: evalScope,
            source_location_id: evalScope === "line" ? sourceLocationId : null,
            eval_date: hazard.eval_date || getToday(),
            approve_date: hazard.approve_date || getToday(),
            improvement: shouldSaveImprovement
              ? {
                  improve_plan: action,
                  emp_id: measure.emp_id ? Number(measure.emp_id) : null,
                  expected_date: measure.dueDate || null,
                  improve_before: beforeUrl,
                  improve_after: afterUrl,
                  improve_after_risk:
                    isCompleted && measure.afterRisk
                      ? Number(measure.afterRisk)
                      : null,
                  comp_date: isCompleted
                    ? measure.compDate || getToday()
                    : null,
                  improve_status: isCompleted ? 20 : 10,
                }
              : null,
          };
        })
      );

      const response = await axiosInstance.post(
        `/risk/${group_code}/${project_code}/wizard/risk-evaluations`,
        {
          eval_date: getToday(),
          approve_date: getToday(),
          wizard_step: 4,
          evaluations: evaluationsPayload,
        }
      );

      clearCurrentDraft();

      let nextPendingGroups = [];
      let reloadFailed = false;

      try {
        nextPendingGroups = await reloadPendingHazards();
      } catch {
        reloadFailed = true;
      }

      const savedRows = Array.isArray(response.data?.data)
        ? response.data.data
        : [];
      const improvementCount = savedRows.filter(
        (row) =>
          Number(row.requires_improve || 0) === 1 || row.improvement_saved
      ).length;

      setSubmitted(true);
      setCompletionSummary({
        submittedCount: hazardsForSubmit.length,
        savedCount: savedRows.length || hazardsForSubmit.length,
        improvementCount,
        pendingCount: reloadFailed
          ? null
          : countHazardsInGroups(nextPendingGroups),
        reloadFailed,
        submittedAt: new Date().toISOString(),
      });
      setDraftId(createDraftId());
      setActiveStep(STEPS.length - 1);
      toast.success("위험성평가 내용이 저장되었습니다.");
    } catch (error) {
      const message =
        error?.response?.data?.message || "저장 중 문제가 발생했습니다.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleStepClick = (targetStep) => {
    if (targetStep > 0 && !selectedHazards.length) {
      toast.warning("위험요인을 먼저 선택해주세요.");
      return;
    }
    if (targetStep > 1 && !hasRiskCriteria) {
      toast.warning("가능성, 중대성, 허용 기준을 먼저 불러와야 합니다.");
      return;
    }
    if (targetStep > 1 && evaluationMissingItems.length) {
      toast.warning("가능성과 중대성을 먼저 입력해주세요.");
      return;
    }
    if (targetStep > 2 && missingItems.length) {
      toast.warning("감소대책의 누락 항목을 먼저 입력해주세요.");
      return;
    }
    setActiveStep(targetStep);
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        p: { xs: 1.5, md: 3 },
        overflow: "auto",
        bgcolor: "#f7f8fb",
      }}
    >
      <DraftPickerDialog
        open={draftPickerOpen}
        drafts={drafts}
        improvementItems={workItems.improvementItems}
        pendingCount={workItems.pendingCount}
        workItemsLoading={workItemsLoading}
        workItemsError={workItemsError}
        onSelect={selectDraft}
        onNew={startNewDraft}
        onDelete={deleteDraft}
        onSelectWorkItem={selectWorkItem}
      />
      <Stack spacing={2} sx={{ width: "100%", maxWidth: 900, mx: "auto" }}>
        <WizardHeader
          activeStep={activeStep}
          progress={progress}
          onStepClick={completionSummary ? () => {} : handleStepClick}
          focusRequest={focusRequest}
        />

        <Paper
          elevation={0}
          sx={{
            ...shellSx,
            p: { xs: 2, md: 2.5 },
            minHeight: isMobile ? "auto" : 520,
          }}
        >
          {completionSummary ? (
            <CompletionPanel
              summary={completionSummary}
              pendingLoading={pendingLoading}
              pendingError={pendingError}
              onContinue={continuePendingEvaluation}
              onViewProgress={viewWizardProgress}
            />
          ) : (
            <>
              {activeStep === 0 ? (
                <HazardConnectionStep
                  groups={groups}
                  selectedHazardIds={selectedHazardIds}
                  onToggleHazard={toggleHazard}
                  onToggleHazardSet={toggleHazardSet}
                  onAddOpen={() => setAddDialogOpen(true)}
                  pendingLoading={pendingLoading}
                  pendingError={pendingError}
                  improvementGroups={improvementGroups}
                  measures={measures}
                />
              ) : null}
              {activeStep === 1 ? (
                <RiskEvaluationStep
                  selectedHazards={selectedHazards}
                  evaluations={evaluations}
                  riskCriteria={riskCriteria}
                  criteriaLoading={criteriaLoading}
                  criteriaError={criteriaError}
                  currentHazardIndex={currentHazardIndex}
                  onCurrentHazardIndexChange={setCurrentHazardIndex}
                  onEvaluationChange={updateEvaluation}
                />
              ) : null}
              {activeStep === 2 ? (
                <MeasureStep
                  selectedHazards={selectedHazards}
                  evaluations={evaluations}
                  riskCriteria={riskCriteria}
                  measures={measures}
                  employees={employees}
                  missingItems={missingItems}
                  onMeasureChange={updateMeasure}
                />
              ) : null}
              {activeStep === 3 ? (
                <ReviewStep
                  selectedHazards={selectedHazards}
                  evaluations={evaluations}
                  riskCriteria={riskCriteria}
                  measures={measures}
                  missingItems={missingItems}
                  submitted={submitted}
                />
              ) : null}
            </>
          )}
        </Paper>

        {!completionSummary ? (
          <Paper
            elevation={0}
            sx={{
              ...shellSx,
              p: 1.4,
              position: { xs: "sticky", sm: "static" },
              bottom: { xs: 8, sm: "auto" },
              zIndex: { xs: 2, sm: "auto" },
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
            >
              <Button
                startIcon={<ArrowBackIcon />}
                color="inherit"
                disabled={activeStep === 0}
                onClick={goPrev}
              >
                이전
              </Button>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                {activeStep === STEPS.length - 1 ? (
                  <Tooltip
                    title={
                      submitted
                        ? "이미 승인 완료된 평가입니다."
                        : saving
                        ? "저장 중입니다."
                        : missingItems.length > 0
                        ? "누락 항목을 먼저 해결하세요."
                        : ""
                    }
                    disableHoverListener={!(submitted || saving || missingItems.length > 0)}
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<FactCheckIcon />}
                        disabled={submitted || saving || missingItems.length > 0}
                        onClick={submit}
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                      >
                        {saving ? "저장 중" : submitted ? "승인 완료" : "승인"}
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={goNext}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    다음 단계
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        ) : null}
      </Stack>

      <AddHazardDialog
        open={addDialogOpen}
        groups={groups}
        onClose={() => setAddDialogOpen(false)}
        onAdd={addHazard}
      />
    </Box>
  );
}
